/**
 * AI Prompt Templates
 * Pre-built prompts for different AI-powered features.
 *
 * DSL documentation in prompting:
 * - getDSLSyntaxReference() is the single source of truth and includes the full content needed from
 *   docs/DSL_AI_REFERENCE.md: rules, config, built-ins, stats by (first/last/first_when), last bar of
 *   session pattern, trading date pattern, summary block, and error fix table. The in-app assistant
 *   therefore has access to the full doc (no separate fetch).
 * - Full reference is included in: script generation (reasoning, code, correction), script editing
 *   (complete code, change proposal), adaptive prompts (code_improvement, error_fix, code_generation,
 *   learn_dsl, learn_algorithmic_trading, suggestion_request when script present), AIScriptEditor chat.
 * - Short DSL reminder (no full reference) in: code_explanation (when script present), methodology_question,
 *   analysis_question default, generateEditReasoningPrompt, AIResultsExplanation initial explanation.
 * - generateAnalysisSuggestionsPrompt includes full reference so "How: DSL pattern" suggestions are valid.
 * When adding new prompts that output or suggest DSL code, include getDSLSyntaxReference() or a short reminder.
 */

/** Context for frustration detection (e.g. script has errors, user already had a bad experience). */
export interface FrustrationDetectionContext {
  hasCompilationErrors?: boolean;
  /** Last assistant message indicated failure or error. */
  lastAssistantWasError?: boolean;
}

/** Keywords and phrases that suggest the user is frustrated or upset (Spanish and English). */
const FRUSTRATION_INDICATORS = [
  'no funciona', 'doesn\'t work', 'no sirve', 'no entiende', 'frustrado', 'frustrated', 'molesto', 'angry', 'enojado',
  'no puedo', 'can\'t', 'cannot', 'siempre falla', 'keeps failing', 'otra vez', 'again', 'nunca', 'never',
  'por qué', 'why', 'qué mal', 'how come', 'no tiene sentido', 'ridículo', 'ridiculous', 'horrible',
  'ayuda de verdad', 'real help', 'en serio', 'seriously', 'ya probé', 'i already tried', 'no me ayuda', 'doesn\'t help',
  '!!!', '??', '😤', '😠', '🤬', 'urgente', 'urgent', 'desesperado', 'desperate',
];

/**
 * Detect if the user message or context suggests frustration. When true, the assistant should
 * respond with warmth, empathy, and clear step-by-step instructions.
 */
export function detectUserFrustration(
  userMessage: string,
  context?: FrustrationDetectionContext
): boolean {
  const text = (userMessage || '').trim().toLowerCase();
  if (text.length === 0) return false;

  const hasIndicator = FRUSTRATION_INDICATORS.some((ind) => text.includes(ind.toLowerCase()));
  const hasMultipleCaps = (text.match(/\b[A-Z]{2,}\b/g) || []).length >= 2;
  const hasRepeatedPunctuation = /[!?]{2,}|\.{3,}/.test(text);
  const contextSuggestsFrustration = context?.hasCompilationErrors === true || context?.lastAssistantWasError === true;

  return hasIndicator || (contextSuggestsFrustration && (hasMultipleCaps || hasRepeatedPunctuation || text.length < 100));
}

/**
 * Instructions to prepend to the system prompt when the user seems frustrated.
 * Tells the model to respond with empathy, warmth, and clear resolution steps.
 */
export function getWarmResponseInstructions(): string {
  return `=== WHEN THE USER SEEMS FRUSTRATED OR UPSET ===

Your priority is to be **human, warm, and helpful**.

1. **Acknowledge first**: Briefly acknowledge that you hear them and that it's frustrating (e.g. "I get that this is frustrating", "Entiendo que esto es molesto").
2. **Stay calm and kind**: Use a calm, supportive tone. Avoid sounding robotic or dismissive. One short sentence of empathy is enough—then focus on solving.
3. **Give clear steps**: Provide **concrete, numbered steps** to resolve the issue. Each step should be one clear action (e.g. "1. Open the script and check that the first block is \`config:\`", "2. Replace any \`? :\` with \`if ... then ... else ...\`").
4. **One thing at a time**: If there are several possible causes, start with the most likely fix. Offer to go step-by-step if they want.
5. **End with next step**: Finish with what they should do right now (e.g. "Try changing that line and run **Compile & fix with AI** again", "Prueba eso y dime qué error sale ahora").

Keep technical accuracy (DSL rules, syntax) but wrap it in a warm, human tone. Do not be condescending or over-apologetic; be a supportive expert.
`;
}

export function getDSLSyntaxReference(): string {
  return `=== QUANTQUACK DSL SYNTAX REFERENCE (use this for all code suggestions) ===

📋 GOLDEN RULES:
1. No implicit state: no var, no bar_index, no barstate. Declarative only.
2. Inputs are constants: "input name = value" — value MUST be a literal (number, string, bool), NOT an expression.
3. Conditional: "x = if condition then a else b" — never use "? :" (invalid in DSL).
4. Historical access: only past with literal index: close[1], high[2]. Never close[n] with variable n, never future.
5. One "config:" block at the very start; one "output" at the end.

⚙️ CONFIG (required at top of script):
  config:
    timezone: "UTC"                    # or "America/New_York", "Europe/London", etc.
    session_strategy: "trading_sessions"   # do not forget when using sessions (or "custom"/"by_date")
    sessions:                          # optional, for session-based analysis
      - name: "premarket"
        start_time: "04:00"
        end_time: "09:30"
        description: "Pre-market"
      - name: "regular"
        start_time: "09:30"
        end_time: "16:00"
        description: "Regular hours"
    interval_duration: 90              # minutes per bar (optional)
  # Optional: backtesting (capital, signal_timeframe, execution_timeframe, position_size, risk_management)

📊 COLUMNS:
  Prices: open, high, low, close, volume. Time: timestamp.
  Structural (if present in dataset): date, session, interval_number, symbol.

🔧 VARIABLES:
  name = expression   (define before use). Example: ma = sma(close, 20)

📈 LAG (historical only, literal index):
  close[1]   # previous bar. close[2] = two bars ago. No close[variable], no future.

🔀 CONDITIONAL:
  x = if condition then value_if_true else value_if_false   (NOT ? :)

📦 BUILT-IN FUNCTIONS:
  Technical: sma(series, period), ema(series, period), atr(period), vwap(), abs(x)
  Crosses: crossover(series1, series2), crossunder(series1, series2)
  Pivots: pivothigh(source, leftBars, rightBars), pivotlow(source, leftBars, rightBars) — leftBars/rightBars integer literals
  Windows: highest(series, period), lowest(series, period)
  Session/window: count_consecutive(condition), max_since(condition, series), min_since(condition, series), high_since(condition), low_since(condition), accumulate_while(condition, value), carry_while(condition, value), carry_forward(condition, value), flag_once(condition), latch(condition), latch(condition, reset). carry_while = first value when condition true in segment, na when false. carry_forward = last value when condition true, propagated forward (LOCF).
  Time: hour(timestamp), weekday(timestamp), month(...), day_of_month(...), year(...), minute(timestamp)
  Math: max(a,b), min(a,b), round(x), floor(x), ceil(x), sqrt(x), pow(x,y), log(x), exp(x)

🎯 SIGNALS:
  signal name = boolean_expression
  Example: signal entry = crossover(sma(close, 10), sma(close, 20))

📊 AGGREGATIONS (stats by) — grouping columns must exist in dataset (e.g. date, session, symbol):
  stats by date:
    count(*) as total              # all rows; count(condition) counts where condition is true
    percent(condition) as alias   # fraction 0.0–1.0 where condition is true
    avg(expr), max(expr), min(expr), stddev(expr), median(expr)
    first(expr) as alias          # first value in group (order by timestamp)
    last(expr) as alias           # last value in group
    first_when(value, condition) as alias   # first value where condition is true in group
  Example:
    stats by session:
      count(breakout) as breakouts
      percent(close > open) as up_pct
      first(timestamp) as first_ts
      last(close) as session_close

📤 OUTPUT (exactly one at end):
  output table   # all calculated columns
  output stats   # aggregation results only
  output signals # rows where any signal is true

⚙️ INPUTS:
  input name = literal   e.g. input period = 20   input threshold = 0.02   input sym = "AAPL" as string
  Default value must be a literal, NOT an expression (e.g. NOT input x = n + 1).

🔧 CUSTOM FUNCTIONS (optional):
  func name(p: type, ...) -> return_type = expression   (pure, no recursion, single expression)

❌ INVALID:
  - ? : (use if then else)
  - input x = expression (only literals)
  - close[n] with n variable or negative (only close[1], close[2], ...)
  - stats() = {...} or signals = {...} or object literals
  - "param" (use "input")
  - Loops, mutable state, future access

⚠️ COMMON ERRORS TO FIX:
  Undefined variable → define variable before use.
  Type mismatch → check function signatures (series vs scalar).
  Input with expression → use literal: input period = 20.
  Wrong conditional → use "if cond then a else b", not "cond ? a : b".
  Missing config/output → add config: at top and one output at end.
  stats by unknown column → use only columns in dataset (date, session, symbol, etc.).

📝 MINIMAL SCRIPT SHAPE:
  config:
    timezone: "UTC"
    interval_duration: 60
  input period = 20
  ma = sma(close, period)
  signal cross = crossover(sma(close, 9), sma(close, 21))
  stats by date:
    count(cross) as crosses
    percent(close > open) as up_pct
  output stats

📌 LAST BAR OF SESSION (no lookahead): DSL has no next()/LEAD. To mark last bar of each session use:
  end_ts = session_end_timestamp(session)
  bar = bar_size()
  is_session_end_bar = (end_ts - timestamp) >= 0 and (end_ts - timestamp) <= bar
  (One-liner: is_session_end_bar = (session_end_timestamp(session) - timestamp) >= 0 and (session_end_timestamp(session) - timestamp) <= bar_size())

📌 TRADING DATE (e.g. CME 18:00 NY): timestamp in seconds. If dataset uses ms, use timestamp/1000 in time_to_utc.
  utc = time_to_utc(timestamp)
  trading_date = date_from_epoch(utc + 6 * 3600)
  stats by trading_date: count(*) as bars   output stats

📌 SUMMARY BLOCK (optional): After stats by, add "summary:" for one row of aggregations over the stats by result. Reference only column aliases from stats by. Use count, percent, avg, max, min, first, last, first_when — no prev/lead.

📋 ERROR FIX TABLE:
  Undefined variable → define before use or fix typo.
  Type mismatch → check series vs scalar in function signature.
  Input with expression → use literal only: input period = 20.
  Wrong conditional → use "if cond then a else b", not "? :".
  Missing config/output → add config at top, one output at end.
  stats by unknown column → group only by dataset columns (date, session, symbol, etc.).
  If you use summary:, it must come after stats by; reference only aliases from that stats by.
`;
}

export interface ScriptGenerationContext {
  userRequest: string;
  existingSignals?: string[];
  datasetInfo?: {
    symbol?: string;
    timeframe?: string;
    columns?: string[];
  };
}

/** Market segment for strategy suggestions (user selects before/during exploration). */
export type MarketSegment = 'forex' | 'equities' | 'futures' | 'crypto' | 'other';

/** Context for autonomous strategy exploration (optional goal; agent can decide what to explore). */
export interface AutonomousStrategyContext {
  /** Optional short goal, e.g. "momentum in London session", "RSI oversold bounces". If empty, agent chooses. */
  goal?: string;
  datasetInfo?: {
    symbol?: string;
    timeframe?: string;
    columns?: string[];
  };
  /** Short summaries of strategies already explored this session (e.g. first line of STRATEGY). Agent will prefer a different one. */
  recentStrategySummaries?: string[];
  /** Market segment so the agent prefers strategies that fit (e.g. session/ICT for forex, VWAP/gaps for equities). */
  marketSegment?: MarketSegment;
}

/** Strategy options for "no goal" exploration; order is shuffled each time to encourage variety. */
const AUTONOMOUS_STRATEGY_OPTIONS = [
  'IB/IBR (Initial Balance: first segment high/low, did price take IB high or low later in session)',
  'Session-vs-session bias (NY PM vs NY AM, Asia/London taking NY PM liquidity)',
  'Asian range manipulation (London/NY sweeping Asia high/low then reversing)',
  'ICT (FVG, liquidity sweeps, order blocks, BOS/CHoCH)',
  'Weekly opening gaps or small-cap gappers (gap %, gap fill, continuation vs reversal)',
  'VWAP bands and reversion to VWAP',
  'Tendencial (holding above VWAP/session open) vs reversion a la media (revert to VWAP, OR mid, IB)',
  'Liquidity (volume spikes, equal highs/lows)',
  'Session-based structure (which session opens/closes range, London vs NY bias)',
  '90min / structural cycles (Q1–Q4 bias, Q1 range extension in Q2–Q4)',
];

function shuffleStrategyOptions(): string[] {
  const copy = [...AUTONOMOUS_STRATEGY_OPTIONS];
  for (let i = copy.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy;
}

/**
 * Trading knowledge and exploration mindset for the autonomous strategy agent.
 * Favours logical, non-generic strategies: IB/IBR, bias, liquidity, session structure,
 * Asian range manipulation, ICT (FVG, sweeps, order blocks), weekly gaps, small-cap gappers,
 * VWAP bands, tendencial vs reversion a la media—not EMA crossovers or plain RSI.
 */
export function getTradingStrategyAgentKnowledge(): string {
  return `=== TRADING STRATEGY AGENT: KNOWLEDGE & EXPLORATION ===

**Your role:** You are an autonomous trading strategy researcher with a preference for institutional/smart-money style ideas. You generate complete, runnable strategies. When no goal is given, you MUST choose a concrete strategy from the PREFER list below—never generic retail ideas.

**AVOID (do not propose as primary strategy):**
- Simple EMA/SMA crossovers (e.g. "EMA 9 crosses EMA 21")
- Generic RSI overbought/oversold (e.g. "buy when RSI < 30, sell when RSI > 70") unless combined with session/liquidity/IB context
- Standalone "MACD crossover" or "Bollinger bounce" without a clear structural or session thesis
- Vague "momentum" or "mean reversion" without defining what structure (session, range, liquidity) you are trading

**PREFER — propose these when exploring (with or without user goal):**
- **Bias strategies:** Session-vs-session bias (e.g. NY PM high > NY AM high = bullish bias; NY PM low < NY AM low = bearish). Who takes whose liquidity: Asia taking NY PM high/low; London (next day) targeting NY PM levels. Use stats by date, session column (DQ1–DQ4 or custom), per-session high/low with na, optional LEAD for next-session metrics.
- **Liquidity strategies:** Equal highs/lows (liquidity pools), volume profile, volume spikes vs average, market impact / slippage estimates, absorption (large volume without proportional move). Use volume, sma(volume), liquidity_score style metrics, large_volume_event, flow direction, order size vs daily volume.
- **Session-based structure:** Which session opens the range, which closes it; London vs NY bias; Asia consolidating vs taking liquidity; session_end_timestamp(session), hour(timestamp), bar_size(), stats by date with per-session aggregates.
- **90min / structural cycles:** Quarters of session (Q1–Q4), interval_number, interval_duration; bias per quarter; Q1 range extension in Q2–Q4.
- **IB / IBR (Initial Balance / Initial Balance Range):** First segment of session (e.g. first 90 min via interval_number, Q1) defines IB high/low; bias = IB close vs IB open (bullish/bearish). Strategies: did price take IB high or IB low later in session? Use count_consecutive(interval_number == 1), session, first bar of Q1 vs last bar of Q1, then stats by date/session.
- **Asian range manipulation (ICT-style):** Asian session defines a range (Asia high/low); London or NY often "manipulates" by sweeping Asian high or low (fake breakout) then reversing. Measure: did price take Asian high then close below? Asian low then close above? Use per-session high/low (Asia), then later sessions; stats by date.
- **ICT concepts:** Fair Value Gaps (FVG: low[0] > high[2] = bullish gap; high[0] < low[2] = bearish gap); liquidity sweeps (equal highs/lows then reversal); order blocks; break of structure (BOS) / change of character (CHoCH). Use high, low, open, close, [1], [2]; detect gaps, sweep of level then close beyond.
- **Weekly opening gaps:** Monday open vs Friday close; gap size (%), gap fill (did price fill the gap?), gap direction. For small caps: **gappers** = names that gap up/down at open; continuation vs reversal, volume on gap. Use date, day-of-week, open vs previous close; stats by date or by symbol if multi-symbol.
- **Small caps / gappers:** Gapper strategies: gap % at open, momentum continuation vs gap fade, volume on gap day. Filter by gap size; measure win rate when gap > X% and price holds vs fills.
- **VWAP bands:** VWAP as level; deviation bands (VWAP ± 1 or 2 std); price above/below VWAP (trend); **reversion to VWAP** (mean reversion when stretched). Use vwap(), distance from VWAP, bands.
- **Tendencial vs reversion a la media:** **Tendencial:** price extending; holding above VWAP or session open; higher highs / lower lows. **Reversion:** price reverting to VWAP, opening range mid, IB, or session high/low; FVG fill, liquidity sweep then reversal. Define the "mean" (VWAP, OR mid, IB) and structure (session, day).

**Concepts you can use in code:**
- **Sessions:** session column, session_end_timestamp(session), hour(timestamp), config.sessions (DQ1–DQ4 etc.), stats by date.
- **IB/IBR:** interval_number, count_consecutive, first_when / last bar of segment, conditional high/low (if is_q1 then high else na).
- **Bias:** Compare session highs/lows across sessions (e.g. dq3_high > dq2_high), aggregate by date.
- **Liquidity:** volume, sma(volume), volume_ratio, liquidity thresholds, large_volume_event, range vs volume.
- **Asian range / manipulation:** Per-session high/low (asia_high, asia_low); later session: did high > asia_high and close < asia_high (sweep then reject)? Use stats by date, session.
- **ICT / FVG:** Bullish FVG: low > high[2]. Bearish FVG: high < low[2]. Sweep: low < prev_low then close > prev_high (liquidity grab). Use [1], [2], latch, bars_since.
- **Gaps:** open vs close[1] or previous day close; gap_pct = (open - prev_close) / prev_close * 100; gap fill = did low <= prev_close (up gap) or high >= prev_close (down gap)? Day of week for weekly gap (Monday open vs Friday close).
- **VWAP / bands:** vwap(), deviation = close - vwap(); bands = vwap() ± k * stddev or ATR; mean reversion = signal when |deviation| > threshold then reversion.
- **Signals:** signal name = expression; latch(condition), latch(condition, reset); count_consecutive; bars_since.

**Strategy design:**
- **Entry/exit:** Clear boolean signals (e.g. long_entry, short_entry). Use built-ins (latch, count_consecutive, bars_since) instead of self-referential series.
- **Risk (optional):** config backtesting with stop_loss, take_profit, position_size when relevant.
- **Exploration:** NEXT_EXPLORATIONS must be 2–3 concrete ideas in the same family (e.g. "IB bias with volume filter", "Same bias but London targeting NY PM only", "Liquidity sweep then reversal").

**Exploration capacity:**
- When goal is empty: Pick exactly one strategy from the PREFER list (IB, bias, Asian manipulation, ICT, weekly gaps/gappers, VWAP bands, tendencial/reversion, liquidity, session-structure). Never default to EMA crossover or plain RSI.
- Always end your reasoning with a short "NEXT_EXPLORATIONS:" block: 2–3 one-line ideas for what to try next (different params, sessions, or indicators).
- Generate the full DSL script for the chosen strategy; the code must compile and run.
`;
}

/**
 * Prompt for the autonomous agent reasoning phase: decide what strategy to build and what to explore next.
 */
const MARKET_SEGMENT_GUIDANCE: Record<MarketSegment, string> = {
  forex: 'Session-based structure, Asian range manipulation, London/NY bias, ICT (FVG, sweeps), session-vs-session bias, kill zones. Avoid: small-cap gappers, VWAP bands (no single VWAP for spot forex unless volume is available).',
  equities: 'VWAP bands and reversion to VWAP, weekly opening gaps, small-cap gappers, opening range (OR) breakout/failure, MOC/closing range, volume spikes, liquidity. Session still useful for US session vs premarket/afterhours.',
  futures: 'IB/IBR, session-based structure, Asian range manipulation, liquidity (volume), 90min/Q1–Q4 cycles, session-vs-session bias, ICT. Very session-driven; opening range and kill zones apply.',
  crypto: 'ICT (FVG, sweeps, order blocks), liquidity, tendencial vs reversion. Session is optional (some venues 24/7); structure and liquidity matter more.',
  other: 'Any strategy from the list; prefer ones that match the data (e.g. if volume/sessions exist, use them).',
};

export function generateAutonomousStrategyReasoningPrompt(context: AutonomousStrategyContext): string {
  const { goal = '', datasetInfo, recentStrategySummaries, marketSegment } = context;
  const hasGoal = goal.trim().length > 0;

  let prompt = `You are an autonomous trading strategy agent with full trading knowledge. Your task is to decide what strategy to implement and what to explore next.
`;
  if (datasetInfo) {
    prompt += `\n📊 DATASET:`;
    if (datasetInfo.symbol) prompt += ` ${datasetInfo.symbol}`;
    if (datasetInfo.timeframe) prompt += ` ${datasetInfo.timeframe}`;
    if (datasetInfo.columns?.length) prompt += ` | Columns: ${datasetInfo.columns.join(', ')}`;
    prompt += `\n`;
  }
  if (marketSegment && marketSegment !== 'other') {
    const label = marketSegment === 'equities' ? 'US Equities' : marketSegment === 'forex' ? 'Forex' : marketSegment.charAt(0).toUpperCase() + marketSegment.slice(1);
    prompt += `\n**Market segment:** ${label}. Prefer strategies that fit this market: ${MARKET_SEGMENT_GUIDANCE[marketSegment]}\n`;
  }
  if (hasGoal) {
    prompt += `\n**User goal (focus here):** "${goal.trim()}"\n`;
  } else {
    const options = shuffleStrategyOptions();
    const optionsList = options.map((o, i) => `${i + 1}. ${o}`).join('\n');
    prompt += `\n**No specific goal:** Choose ONE strategy from the following list (order randomized each time—do NOT always pick the first). Vary your choice: prefer a strategy you have not used recently.\n`;
    if (recentStrategySummaries && recentStrategySummaries.length > 0) {
      prompt += `\n**Already explored in this session (pick something different):**\n${recentStrategySummaries.map((s) => `- ${s}`).join('\n')}\n`;
    }
    prompt += `\n**Strategies to choose from (pick one):**\n${optionsList}\n`;
    prompt += `\nDo NOT choose generic EMA crossover or plain RSI. Pick one from the list above and justify in 2–3 sentences.\n`;
  }

  prompt += `\n**Output format (strict):**
1. **STRATEGY:** One paragraph describing the strategy (what you will code: indicators, entry/exit logic, session/time filters if any).
2. **RATIONALE:** Why this is interesting or what we will learn from it.
3. **NEXT_EXPLORATIONS:** Exactly 2–3 one-line ideas for follow-up in the same family (e.g. "Asian range manipulation with volume filter", "VWAP bands + reversion in London only", "Weekly gap fill rate by symbol", "FVG then sweep of Asian low"). One per line.

No code yet; next step will be code generation.`;

  return prompt;
}

/**
 * Prompt for the autonomous agent code phase: generate full DSL from reasoning, including exploration hints.
 */
export function generateAutonomousStrategyCodePrompt(
  context: AutonomousStrategyContext,
  reasoning: string
): string {
  const { goal = '', datasetInfo } = context;

  let prompt = `You are an autonomous trading strategy agent. Generate the complete QuantQuack DSL script from the reasoning below.

REASONING:
${reasoning}
`;

  if (goal.trim()) {
    prompt += `\n**User goal:** "${goal.trim()}"\n`;
  }
  if (datasetInfo) {
    prompt += `\n📊 DATASET:`;
    if (datasetInfo.symbol) prompt += ` ${datasetInfo.symbol}`;
    if (datasetInfo.timeframe) prompt += ` ${datasetInfo.timeframe}`;
    prompt += `\n`;
  }

  prompt += `\n${getDSLSyntaxReference()}
${getTradingStrategyAgentKnowledge()}

**Task:** Output ONLY the full DSL script (config, inputs, variables, signals, stats by if needed, output). No markdown, no code fences, no explanation before or after the code.
**Requirements:** Script must compile; use session/hour/session_end_timestamp where session logic is needed; use built-ins (count_consecutive, bars_since, flag_once, latch, etc.) instead of self-reference. End with exactly one output (output table, output stats, or output signals).

Generate the complete DSL script now:`;

  return prompt;
}

/**
 * Parse "NEXT_EXPLORATIONS:" block from autonomous agent reasoning text (one idea per line).
 */
export function parseNextExplorations(reasoningText: string): string[] {
  const match = reasoningText.match(/NEXT_EXPLORATIONS?:\s*([\s\S]*?)(?=\n\n|$)/i);
  if (!match) return [];
  const block = match[1].trim();
  return block
    .split(/\n/)
    .map((line) => line.replace(/^\s*[-*]\s*/, '').replace(/^\d+\.\s*/, '').trim())
    .filter((line) => line.length > 0)
    .slice(0, 5);
}

/** Extract a short summary of the STRATEGY block from reasoning text (for "already explored" variety). */
export function parseStrategySummaryFromReasoning(reasoningText: string): string | null {
  const match = reasoningText.match(/STRATEGY:\s*([\s\S]*?)(?=\n\s*\*\*[A-Z]+:|$)/i);
  if (!match) return null;
  const block = match[1].trim();
  const firstLine = block.split(/\n/)[0]?.trim() ?? block;
  const summary = firstLine.length > 120 ? firstLine.slice(0, 117) + '...' : firstLine;
  return summary.length > 0 ? summary : null;
}

export interface ResultsExplanationContext {
  results: Record<string, any>;
  scriptDescription?: string;
  scriptCode?: string;
  datasetInfo?: {
    symbol?: string;
    timeframe?: string;
    columns?: string[];
    timezone?: string;
    sessionStrategy?: 'by_date' | 'custom' | 'trading_sessions';
    sessions?: Array<{
      name: string;
      start_time: string;
      end_time: string;
      description?: string;
    }>;
    sessionIncludeDate?: boolean;
    intervalDuration?: number | string;
    intervalIdFormat?: 'timestamp' | 'datetime';
  };
  resultType?: 'table' | 'stats' | 'signals' | 'backtest';
}

export interface PatternDetectionContext {
  data: Array<Record<string, any>>;
  signals?: string[];
  metrics?: string[];
}

export interface AnalysisSuggestionsContext {
  currentAnalysis: {
    script?: string;
    results?: Record<string, any>;
    signals?: string[];
  };
  datasetInfo?: {
    symbol?: string;
    timeframe?: string;
    availableColumns?: string[];
  };
}

/**
 * Generate prompt for reasoning phase (Phase 1)
 * This prompt asks the AI to reason about what the user is investigating
 */
export function generateReasoningPrompt(context: ScriptGenerationContext): string {
  const { userRequest, existingSignals, datasetInfo } = context;
  
  let prompt = `You are an expert quantitative analyst working as an AI agent. Your first task is to reason about what the user is investigating.

User Request: "${userRequest}"`;

  // Add dataset context
  if (datasetInfo) {
    prompt += `\n\n📊 MARKET DATA CONTEXT:`;
    if (datasetInfo.symbol) prompt += `\n- Instrument: ${datasetInfo.symbol}`;
    if (datasetInfo.timeframe) prompt += `\n- Timeframe: ${datasetInfo.timeframe}`;
    if (datasetInfo.columns && datasetInfo.columns.length > 0) {
      prompt += `\n- Available data columns: ${datasetInfo.columns.join(', ')}`;
    }
    prompt += `\n- Standard OHLCV data is always available: open, high, low, close, volume, timestamp`;
  }
  
  if (existingSignals && existingSignals.length > 0) {
    prompt += `\n\n📌 EXISTING SIGNALS IN SCRIPT: ${existingSignals.join(', ')}`;
  }

  prompt += `\n\n🤔 REASONING TASK:

Analyze what the user is investigating and provide a structured reasoning. Think through:

1. **Research Intent**: What is the user trying to discover or analyze?
   - Trading opportunities (signals, entries, exits)?
   - Market patterns (breakouts, reversals, trends)?
   - Market behavior (volatility, volume, session effects)?
   - Statistical analysis (aggregations, distributions)?

2. **Market Phenomena**: What market conditions or patterns are they interested in?
   - Price action patterns (breakouts, pullbacks, consolidations)?
   - Indicator-based signals (RSI, moving averages, momentum)?
   - Volume analysis (volume spikes, volume trends)?
   - Time-based patterns (session effects, day-of-week effects)?
   - Volatility patterns (ATR, price ranges)?

3. **Analysis Type**: What type of analysis output do they need?
   - Signal detection (boolean signals for entry/exit)?
   - Statistical metrics (counts, percentages, averages)?
   - Pattern identification (consecutive patterns, since-last patterns)?
   - Comparative analysis (by session, by time, by condition)?

4. **Technical Approach**: What technical indicators or calculations would be most appropriate?
   - Moving averages for trend identification?
   - RSI for momentum/overbought/oversold conditions?
   - ATR for volatility measurement?
   - Highest/lowest for support/resistance levels?
   - Pivothigh/pivotlow for swing pivot detection?
   - Pattern functions for advanced logic?

5. **Dataset Considerations**: How does this relate to the dataset context?
   - What timeframe is appropriate for the analysis?
   - Should session data be considered?
   - What lookback periods make sense for this timeframe?

6. **Actionability**: What would make this analysis actionable?
   - What signals would a trader actually use?
   - What metrics would provide meaningful insights?
   - How should results be structured for decision-making?

Provide a clear, structured reasoning that explains:
- What the user is investigating
- What market phenomena are relevant
- What technical approach should be used
- What the expected output should be

Format your response as a clear analysis plan that can be used to generate the DSL code.`;

  return prompt;
}

export interface SyntaxCorrectionContext {
  originalCode: string;
  compilationErrors: Array<{
    line?: number;
    column?: number;
    message: string;
    type?: string;
  }>;
  /** Errors from the previous correction round (so the model avoids repeating the same fix). */
  previousRoundErrors?: Array<{ line?: number; column?: number; message: string; type?: string }>;
  reasoning?: string;
  userRequest?: string;
}

/**
 * In-memory error→fix and pattern knowledge for the compile-and-fix agent (aligned with .cursor/skills/quantquack-dsl/SKILL.md).
 * Included in the correction prompt so the model applies the right fix for each error type.
 */
export function getDSLFixAgentMemory(): string {
  return `=== DSL FIX AGENT MEMORY (apply these when you see the error or pattern) ===

📋 ERROR → FIX (match error text, then apply):
| Error / symptom | Fix |
|-----------------|-----|
| "Circular dependency" / self-reference | Replace with built-in: count_consecutive(condition), accumulate_while(condition, value), flag_once(condition), bars_since(condition), carry_while(condition, value), carry_forward(condition, value), latch(condition), latch(condition, reset) |
| "Undefined variable" | Define variable earlier in script; check spelling; in func only params and columns are in scope |
| "Unknown function" / "Function 'X' not found" | Check name and arity: sma(series, int), ema(series, int), count_consecutive(series<bool>), bars_since(series<bool>), high_since/low_since(max_since/min_since), accumulate_while(condition, value), carry_while, carry_forward, flag_once, latch(condition), latch(condition, reset), etc. |
| "Type mismatch" / "expected X, got Y" | sma(series<float>, int); count(expr)/percent(expr) need bool; crossover/crossunder(series, series); if then else returns same type in both branches |
| "input ... must be a literal" | Use literal only: input period = 20, not input x = n + 1 |
| "summary block requires a preceding stats by" | Add a "stats by ..." block before summary |
| "prev/lead are only allowed in stats by" | Use prev/lead only inside stats by; in summary use count, avg, max, min, first, last, first_when on stats-by columns |
| "Function 'X' is not pure" | func must be pure: only params, literals, pure built-ins; pass series as param, e.g. func f(c: series<float>) = sma(c, 10) |
| "stats by" / "column X does not exist" | Group only by dataset columns: date, session, symbol; stats aggregates body variables (use aliases from body) |

📌 PINE-LIKE PATTERN → DSL BUILT-IN (replace self-reference with these):
| If you see / intent | Use |
|---------------------|-----|
| cnt = cond ? cnt[1]+1 : 0 | count_consecutive(condition) |
| sum = cond ? sum[1]+val : 0 | accumulate_while(condition, value) |
| state = cond ? true : state[1] | flag_once(condition) or latch(condition, reset) |
| bars_since = cond ? 0 : bars_since[1]+1 | bars_since(condition) |
| First value in segment, na outside | carry_while(condition, value) |
| Last value carried forward | carry_forward(condition, value) |
| Max/min since condition | max_since(condition, series), min_since(condition, series), high_since(cond), low_since(cond) |

❌ ANTIPATTERNS → FIX:
| Don't | Do |
|-------|-----|
| condition ? a : b | if condition then a else b |
| input x = expression | input x = literal (e.g. 20, "UTC", true) |
| close[n] with variable n | close[1], close[2] only; or highest(high, n), lowest(low, n) |
| Complex logic inside stats | Define in body (e.g. is_x = ...), then stats by date: count(is_x) as x |
| x = x[1] + 1 or any self-reference | Use built-in from table above |
| func f() = close | func f(c: series<float>) = c and pass close as argument |
| stats by without config | Add config: at top with timezone, session_strategy (e.g. "trading_sessions") when using sessions, and sessions list |
`;
}

/**
 * Generate prompt for syntax correction (Phase 3)
 * This prompt is used when compilation fails to fix syntax errors
 */
export function generateSyntaxCorrectionPrompt(context: SyntaxCorrectionContext): string {
  const { originalCode, compilationErrors, previousRoundErrors, reasoning, userRequest } = context;
  
  let prompt = `You are an expert QuantQuack DSL developer specializing in fixing compilation errors. Your task is to correct syntax errors in DSL code.

ORIGINAL CODE:
\`\`\`
${originalCode}
\`\`\`

COMPILATION ERRORS (fix these):`;

  compilationErrors.forEach((error, idx) => {
    const location = error.line !== undefined 
      ? `Line ${error.line}${error.column !== undefined ? `, column ${error.column}` : ''}`
      : 'Unknown location';
    prompt += `\n${idx + 1}. ${location}: ${error.message}${error.type ? ` (${error.type})` : ''}`;
  });

  if (previousRoundErrors && previousRoundErrors.length > 0) {
    prompt += `\n\n⚠️ PREVIOUS ROUND HAD THESE ERRORS (you already tried to fix; do not repeat the same approach if it introduced new issues):`;
    previousRoundErrors.forEach((error, idx) => {
      const location = error.line !== undefined
        ? `Line ${error.line}${error.column !== undefined ? `, column ${error.column}` : ''}`
        : 'Unknown location';
      prompt += `\n${idx + 1}. ${location}: ${error.message}`;
    });
  }

  if (reasoning) {
    prompt += `\n\nORIGINAL REASONING/INTENT:
${reasoning}`;
  }

  if (userRequest) {
    prompt += `\n\nORIGINAL USER REQUEST: "${userRequest}"`;
  }

  prompt += `\n\n${getDSLSyntaxReference()}`;
  prompt += `\n\n${getDSLFixAgentMemory()}`;

  prompt += `\n\n🔧 CORRECTION TASK:

Fix all compilation errors in the DSL code. Your corrections must:

1. **Fix All Errors**: Address every compilation error listed above
2. **Preserve Intent**: Maintain the original logic and intent of the code
3. **Follow DSL Syntax**: Use correct QuantQuack DSL syntax as shown in the reference
4. **Common Error Fixes**:
   - Undefined variables: Ensure all variables are defined before use
   - Type mismatches: Use correct types (float, int, bool, string)
   - Function signatures: Match function parameters to their signatures
   - Syntax errors: Fix missing operators, parentheses, brackets
   - Reserved keywords: Don't use reserved words as variable names
   - Signal syntax: Use "signal" keyword correctly
   - Output syntax: Use "output" keyword correctly

5. **Error-Specific Guidance**:
   - If error mentions "not defined": Check variable/function names and ensure they're defined
   - If error mentions "type mismatch": Check types and use appropriate conversions
   - If error mentions "syntax": Check operators, brackets, parentheses
   - If error mentions "function": Check function name and parameter count/types

📝 CORRECTION REQUIREMENTS:
- Generate ONLY the corrected DSL code, no markdown, no explanations, no code blocks
- The code must compile successfully without errors
- Preserve all original functionality and logic
- Only fix errors, don't add new features unless necessary to fix errors
- Use meaningful variable names consistent with the original code

Generate the corrected DSL code now:`;

  return prompt;
}

/**
 * Generate prompt for code generation phase (Phase 2)
 * This prompt uses the reasoning from Phase 1 to generate the actual DSL code
 */
export function generateCodeFromReasoningPrompt(
  context: ScriptGenerationContext,
  reasoning: string
): string {
  const { userRequest, existingSignals, datasetInfo } = context;
  
  let prompt = `You are an expert QuantQuack DSL developer. Based on the reasoning analysis below, generate the complete DSL code.

REASONING ANALYSIS:
${reasoning}

ORIGINAL USER REQUEST: "${userRequest}"`;

  // Add dataset context
  if (datasetInfo) {
    prompt += `\n\n📊 MARKET DATA CONTEXT:`;
    if (datasetInfo.symbol) prompt += `\n- Instrument: ${datasetInfo.symbol}`;
    if (datasetInfo.timeframe) prompt += `\n- Timeframe: ${datasetInfo.timeframe}`;
    if (datasetInfo.columns && datasetInfo.columns.length > 0) {
      prompt += `\n- Available data columns: ${datasetInfo.columns.join(', ')}`;
    }
  }
  
  if (existingSignals && existingSignals.length > 0) {
    prompt += `\n\n📌 EXISTING SIGNALS: ${existingSignals.join(', ')}`;
  }
  
  prompt += `\n\n${getDSLSyntaxReference()}`;

  prompt += `\n\n🎯 CODE GENERATION TASK:

Based on the reasoning analysis above, generate the complete QuantQuack DSL script that implements the research.

**Code Generation Guidelines:**

1. **Structure the Analysis Logically**:
   - Start with indicator calculations
   - Then define intermediate variables
   - Finally create signals and metrics
   - Use meaningful variable names that reflect trading concepts

2. **Use Appropriate Technical Tools**:
   - Moving averages (SMA, EMA) for trend analysis
   - RSI, ATR for momentum and volatility
   - Crossover/crossunder for signal detection
   - Highest/lowest for breakout/resistance identification
   - Pivothigh/pivotlow for swing pivot (S/R) detection
   - Pattern functions (count_consecutive, bars_since) for advanced logic

3. **Create Meaningful Signals**:
   - Use descriptive names (e.g., "golden_cross", "breakout_high", "rsi_oversold")
   - Add direction markers (# @buy, # @sell, # @long, # @short) in comments when direction is clear
   - Combine multiple conditions logically (and, or)
   - Explain the trading logic in comments

4. **Add Contextual Comments**:
   - Explain what market condition is being detected
   - Describe why this signal or metric matters
   - Note any assumptions or parameter choices

5. **Choose Appropriate Output**:
   - "output table" for detailed bar-by-bar analysis with all calculated columns
   - "output signals" for signal detection focused output
   - "output stats" with "stats by" for aggregated statistical analysis

📝 GENERATION REQUIREMENTS:
- Generate ONLY the DSL code, no markdown, no explanations, no code blocks
- The code must be production-ready and syntactically correct
- Follow QuantQuack DSL syntax exactly as shown in the reference
- Ensure all variables are properly defined before use
- If the request is ambiguous, make reasonable assumptions based on common quantitative analysis practices
- **CRITICAL: Generate the COMPLETE, FULL script - DO NOT truncate or cut off the code**
- **Include ALL necessary code: inputs, variables, signals, stats blocks, output statements**
- **The output must be the FULL, COMPLETE script, not a partial snippet**
- **If the script is long, you MUST still output it in full - do not cut it off mid-way**

Generate the complete DSL script now:`;
  
  return prompt;
}

/**
 * Generate prompt for script generation (legacy - now uses two-phase approach)
 * @deprecated Use generateReasoningPrompt and generateCodeFromReasoningPrompt instead
 */
export function generateScriptPrompt(context: ScriptGenerationContext): string {
  const { userRequest, existingSignals, datasetInfo } = context;
  
  let prompt = `You are an expert quantitative analyst and trading strategy developer working as an AI agent. Your role is to:

1. **FIRST**: Act as a reasoning agent to understand what the user is investigating
2. **THEN**: Translate that understanding into QuantQuack DSL code

The user is a quantitative analyst/trader who understands trading strategies, technical indicators, market patterns, and statistical analysis - but may not be familiar with DSL syntax details.

User Request: "${userRequest}"`;

  // Add dataset context with trading perspective
  if (datasetInfo) {
    prompt += `\n\n📊 MARKET DATA CONTEXT:`;
    if (datasetInfo.symbol) prompt += `\n- Instrument: ${datasetInfo.symbol}`;
    if (datasetInfo.timeframe) prompt += `\n- Timeframe: ${datasetInfo.timeframe}`;
    if (datasetInfo.columns && datasetInfo.columns.length > 0) {
      prompt += `\n- Available data columns: ${datasetInfo.columns.join(', ')}`;
    }
    prompt += `\n- Standard OHLCV data is always available: open, high, low, close, volume, timestamp`;
  }
  
  // Add existing signals context
  if (existingSignals && existingSignals.length > 0) {
    prompt += `\n\n📌 EXISTING SIGNALS IN SCRIPT: ${existingSignals.join(', ')}`;
    prompt += `\n- Consider how your analysis relates to these existing signals`;
    prompt += `\n- You can reference these signals in your logic or create complementary signals`;
  }
  
  prompt += `\n\n${getDSLSyntaxReference()}`;

  prompt += `\n\n🤔 PHASE 1: REASONING AS A QUANTITATIVE ANALYST

Before generating code, reason about what the user is investigating. Think through these questions:

**Research Intent Analysis:**
1. What is the user trying to discover or analyze?
   - Are they looking for trading opportunities (signals, entries, exits)?
   - Are they analyzing market patterns (breakouts, reversals, trends)?
   - Are they studying market behavior (volatility, volume, session effects)?
   - Are they doing statistical analysis (aggregations, distributions, correlations)?

2. What market conditions or patterns are they interested in?
   - Price action patterns (breakouts, pullbacks, consolidations)?
   - Indicator-based signals (RSI, moving averages, momentum)?
   - Volume analysis (volume spikes, volume trends)?
   - Time-based patterns (session effects, day-of-week effects)?
   - Volatility patterns (ATR, price ranges)?

3. What type of analysis output do they need?
   - Signal detection (boolean signals for entry/exit)?
   - Statistical metrics (counts, percentages, averages)?
   - Pattern identification (consecutive patterns, since-last patterns)?
   - Comparative analysis (by session, by time, by condition)?

4. What technical indicators or calculations would be most appropriate?
   - Moving averages for trend identification?
   - RSI for momentum/overbought/oversold conditions?
   - ATR for volatility measurement?
   - Highest/lowest for support/resistance levels?
   - Pivothigh/pivotlow for swing pivot detection?
   - Pattern functions for advanced logic?

5. How does this relate to the dataset context?
   - What timeframe is appropriate for the analysis?
   - Should session data be considered?
   - What lookback periods make sense for this timeframe?
   - Are there any dataset-specific considerations?

6. What would make this analysis actionable?
   - What signals would a trader actually use?
   - What metrics would provide meaningful insights?
   - How should results be structured for decision-making?

**Reasoning Process:**
- Start by understanding the core research question
- Identify the market phenomena being investigated
- Determine the appropriate analytical approach
- Select the right technical tools and indicators
- Plan the signal/metric structure
- Consider how results will be used

🎯 PHASE 2: CODE GENERATION

After reasoning through the analysis above, generate the complete QuantQuack DSL script that implements this research.

**Code Generation Guidelines:**

1. **Structure the Analysis Logically**:
   - Start with indicator calculations
   - Then define intermediate variables
   - Finally create signals and metrics
   - Use meaningful variable names that reflect trading concepts

2. **Use Appropriate Technical Tools**:
   - Moving averages (SMA, EMA) for trend analysis
   - RSI, ATR for momentum and volatility
   - Crossover/crossunder for signal detection
   - Highest/lowest for breakout/resistance identification
   - Pivothigh/pivotlow for swing pivot (S/R) detection
   - Pattern functions (count_consecutive, bars_since) for advanced logic

3. **Create Meaningful Signals**:
   - Use descriptive names (e.g., "golden_cross", "breakout_high", "rsi_oversold")
   - Add direction markers (# @buy, # @sell, # @long, # @short) in comments when direction is clear
   - Combine multiple conditions logically (and, or)
   - Explain the trading logic in comments

4. **Add Contextual Comments**:
   - Explain what market condition is being detected
   - Describe why this signal or metric matters
   - Note any assumptions or parameter choices

5. **Choose Appropriate Output**:
   - "output table" for detailed bar-by-bar analysis with all calculated columns
   - "output signals" for signal detection focused output
   - "output stats" with "stats by" for aggregated statistical analysis

📝 GENERATION REQUIREMENTS:
- Generate ONLY the DSL code, no markdown, no explanations, no code blocks
- The code must be production-ready and syntactically correct
- Follow QuantQuack DSL syntax exactly as shown in the reference
- Ensure all variables are properly defined before use
- If the request is ambiguous, make reasonable assumptions based on common quantitative analysis practices
- Think like a quant: focus on actionable insights and meaningful patterns
- **CRITICAL: Generate the COMPLETE, FULL script - DO NOT truncate or cut off the code**
- **Include ALL necessary code: inputs, variables, signals, stats blocks, output statements**
- **The output must be the FULL, COMPLETE script, not a partial snippet**
- **If the script is long, you MUST still output it in full - do not cut it off mid-way**

Now, reason through the user's request and generate the complete DSL script:`;
  
  return prompt;
}

/**
 * Generate prompt for results explanation
 * Enhanced for quantitative analyst perspective
 */
export function generateResultsExplanationPrompt(context: ResultsExplanationContext): string {
  const { results, scriptDescription, scriptCode, datasetInfo, resultType } = context;
  
  let prompt = `# QUANTITATIVE ANALYSIS REQUEST

You are an expert quantitative analyst (quant) with deep expertise in systematic trading, statistical analysis, and market data interpretation. Your task is to analyze these results and provide comprehensive, actionable insights.

## CONTEXT INFORMATION:`;

  // Add dataset information
  if (datasetInfo) {
    prompt += `\n\n### Market Data Context:`;
    if (datasetInfo.symbol) prompt += `\n- **Instrument**: ${datasetInfo.symbol}`;
    if (datasetInfo.timeframe) prompt += `\n- **Timeframe**: ${datasetInfo.timeframe}`;
    if (datasetInfo.timezone) prompt += `\n- **Timezone**: ${datasetInfo.timezone}`;
    if (datasetInfo.columns && datasetInfo.columns.length > 0) {
      prompt += `\n- **Available Columns**: ${datasetInfo.columns.join(', ')}`;
    }
    
    // Add session information if available
    if (datasetInfo.sessionStrategy) {
      prompt += `\n\n### Session Configuration:`;
      prompt += `\n- **Session Strategy**: ${datasetInfo.sessionStrategy}`;
      
      if (datasetInfo.sessionStrategy !== 'by_date' && datasetInfo.sessions && datasetInfo.sessions.length > 0) {
        prompt += `\n- **Defined Sessions**:`;
        datasetInfo.sessions.forEach((session) => {
          prompt += `\n  - ${session.name}: ${session.start_time} - ${session.end_time}`;
          if (session.description) {
            prompt += ` (${session.description})`;
          }
        });
        if (datasetInfo.sessionIncludeDate !== undefined) {
          prompt += `\n- **Session Date Prefix**: ${datasetInfo.sessionIncludeDate ? 'Yes' : 'No'}`;
        }
      } else if (datasetInfo.sessionStrategy === 'by_date') {
        prompt += `\n- **Session Grouping**: One session per calendar date`;
      }
      
      if (datasetInfo.intervalDuration) {
        prompt += `\n- **Interval Duration**: ${datasetInfo.intervalDuration}`;
      }
      if (datasetInfo.intervalIdFormat) {
        prompt += `\n- **Interval ID Format**: ${datasetInfo.intervalIdFormat}`;
      }
      
      prompt += `\n\n**Session Analysis Note**: Results may contain session-based aggregations. Consider how different trading sessions (premarket, regular hours, afterhours) affect patterns and metrics.`;
    }
  }

  // Add script code context (most important for understanding what was analyzed)
  if (scriptCode) {
    prompt += `\n\n### Analysis Script (QuantQuack DSL):
\`\`\`dsl
${scriptCode}
\`\`\`

**Script Analysis Required:**
- Identify what indicators/calculations were performed
- Understand what signals or patterns were detected
- Recognize what statistical aggregations were computed
- Comprehend the analytical methodology and approach
- Note any assumptions or parameter choices made`;
  } else if (scriptDescription) {
    prompt += `\n\n### Script Description:
${scriptDescription}`;
  }

  // Add result type context
  if (resultType) {
    prompt += `\n\n### Result Type: ${resultType}`;
    if (resultType === 'stats') {
      prompt += `\n- **Type**: Statistical aggregation (grouped results)`;
      prompt += `\n- **Interpretation**: These are aggregated metrics, not individual data points`;
    } else if (resultType === 'table') {
      prompt += `\n- **Type**: Table output (all calculated columns per row)`;
      prompt += `\n- **Interpretation**: Each row represents a data point with all calculated values`;
    } else if (resultType === 'signals') {
      prompt += `\n- **Type**: Signal detection output`;
      prompt += `\n- **Interpretation**: Boolean signals indicating entry/exit opportunities`;
    } else if (resultType === 'backtest') {
      prompt += `\n- **Type**: Backtest performance results`;
      prompt += `\n- **Interpretation**: Historical performance metrics and trade statistics`;
    }
  }

  // Add results data (limit size for large datasets)
  const resultsObj = typeof results === 'object' && !Array.isArray(results) ? results : null;
  const hasSummaryStats = resultsObj && 'summaryStatistics' in resultsObj;
  const hasSampleData = resultsObj && 'sampleData' in resultsObj;
  if (hasSummaryStats || hasSampleData) {
    prompt += `\n\n### Results Data (use Summary statistics and Sample data when provided):
- **Summary statistics** (aggregated): Use this for overall metrics when present.
- **Aggregated description**: ${(resultsObj as any).aggregatedDescription ?? 'N/A'}
- **Sample data**: Representative rows; use with summary for full picture.`;
  }
  const resultsStr = JSON.stringify(results, null, 2);
  if (resultsStr.length > 8000) {
    // For very large results, prefer summaryStatistics + sampleData, then truncate rest
    const keys = resultsObj ? Object.keys(resultsObj) : [];
    const sampleSize = Math.min(100, Array.isArray(results) ? results.length : 10);
    const truncatedPayload: Record<string, unknown> = {};
    if (resultsObj?.summaryStatistics) truncatedPayload.summaryStatistics = (resultsObj as any).summaryStatistics;
    if (resultsObj?.aggregatedDescription) truncatedPayload.aggregatedDescription = (resultsObj as any).aggregatedDescription;
    if (resultsObj?.sampleData) truncatedPayload.sampleData = (resultsObj as any).sampleData;
    if (resultsObj?.sampleNote) truncatedPayload.sampleNote = (resultsObj as any).sampleNote;
    if (resultsObj?.rowCount !== undefined) truncatedPayload.rowCount = (resultsObj as any).rowCount;
    const truncatedStr = Object.keys(truncatedPayload).length > 0
      ? JSON.stringify(truncatedPayload, null, 2)
      : JSON.stringify(Array.isArray(results) ? results.slice(0, sampleSize) : resultsObj ?? results, null, 2).substring(0, 4000);
    prompt += `\n\n**Data Size**: ${(resultsObj as any)?.rowCount ?? (Array.isArray(results) ? results.length : keys.length)} rows/entries
${truncatedStr}
${Object.keys(truncatedPayload).length > 0 ? '\n[Full row data omitted; base your analysis on Summary statistics and Sample data above.]' : '...[Results truncated - focus on patterns and key metrics]'}`;
  } else {
    prompt += `\n\n### Full results payload:
${resultsStr}`;
  }

  prompt += `\n\n🎯 QUANTITATIVE ANALYSIS TASK:

You are analyzing these results as an expert quantitative analyst. Provide a comprehensive, structured analysis following this framework:

## ANALYSIS FRAMEWORK:

### 1. **Executive Summary** (2-3 sentences)
   - What is the main finding or conclusion?
   - What is the key takeaway for a trader?
   - What action should be taken based on these results?

### 2. **Statistical Interpretation**
   Analyze the numbers with statistical rigor:
   - **Key Metrics**: Identify the most important metrics and their values
   - **Statistical Significance**: Are these results statistically meaningful? (consider sample size, variance, confidence)
   - **Outliers & Anomalies**: Highlight any unusual values that stand out
   - **Distribution Analysis**: Describe the spread, skewness, or patterns in the data
   - **Benchmark Comparison**: How do these results compare to typical market behavior or expectations?

### 3. **Trading Insights & Actionability**
   Translate statistics into trading decisions:
   - **Actionable Signals**: What specific trading actions do these results suggest?
   - **Risk/Reward Assessment**: What are the potential risks and rewards?
   - **Entry/Exit Implications**: When should positions be entered or exited?
   - **Position Sizing**: What position sizes are appropriate given these results?
   - **Robustness Check**: How reliable are these findings? What could invalidate them?

### 4. **Methodology Evaluation**
   Assess the analytical approach:
   - **Appropriateness**: Was the methodology suitable for the research question?
   - **Strengths**: What did the analysis do well?
   - **Limitations**: What are the constraints or biases?
   - **Improvements**: What alternative methods or enhancements would help?
   - **Validation Needs**: What additional tests or checks are needed?

### 5. **Pattern Recognition**
   Identify meaningful patterns:
   - **Temporal Patterns**: Are there time-based patterns (by session, hour, day)?
   - **Conditional Patterns**: Do patterns change under certain conditions?
   - **Correlations**: What relationships exist between different metrics?
   - **Cyclical Behaviors**: Are there recurring cycles or trends?
   - **Regime Changes**: Do patterns differ across different market regimes?

### 6. **Code & Methodology Improvements** (if script code provided)
   Proactively suggest improvements:
   - **Code Optimizations**: How can the DSL code be improved?
   - **Better Indicators**: What alternative indicators might work better?
   - **Parameter Tuning**: What parameters should be optimized?
   - **Additional Metrics**: What other calculations would add value?
   - **Code Structure**: How can the code be made more maintainable?

### 7. **Next Steps & Recommendations**
   Provide actionable next steps:
   - **Immediate Actions**: What should be done right now?
   - **Further Analysis**: What additional analysis would deepen understanding?
   - **Validation Steps**: How can these results be validated?
   - **Risk Management**: What risk controls should be implemented?
   - **Monitoring**: What should be tracked going forward?

## OUTPUT REQUIREMENTS:

**Format:**
- Use markdown with clear headings (##, ###)
- Use bullet points for lists
- Use **bold** for key metrics and numbers
- Use code blocks for any code suggestions
- Include specific numbers from the results (don't just say "high" - say "15.3%")

**Tone:**
- Professional and quantitative
- Clear and actionable
- Data-driven (cite specific numbers)
- Balanced (acknowledge both strengths and limitations)

**Length:**
- Comprehensive but focused: 400-600 words
- Prioritize actionable insights over descriptions
- Quality over quantity

**Structure:**
Follow the 7-section framework above. Each section should be substantive and specific.

Begin your analysis now:`;
  
  return prompt;
}

/**
 * Generate prompt for pattern detection
 */
export function generatePatternDetectionPrompt(context: PatternDetectionContext): string {
  const { data, signals, metrics } = context;
  
  // Sample data for analysis (limit to avoid token limits)
  const sampleData = data.slice(0, 100);
  
  let prompt = `You are a quantitative analyst. Analyze this trading data and detect patterns automatically.

Data Summary:
- Total rows: ${data.length}
- Sample data (first 100 rows): ${JSON.stringify(sampleData, null, 2)}`;

  if (signals && signals.length > 0) {
    prompt += `\n\nActive Signals: ${signals.join(', ')}`;
  }
  
  if (metrics && metrics.length > 0) {
    prompt += `\n\nMetrics to analyze: ${metrics.join(', ')}`;
  }
  
  prompt += `\n\nDetect and report:
1. Volatility patterns (by time of day, day of week, session)
2. Signal frequency patterns
3. Price movement patterns
4. Volume patterns
5. Any anomalies or outliers

Format your response as a structured list with:
- Pattern Type: [Type]
- Description: [Clear description]
- Evidence: [Key statistics supporting the pattern]
- Significance: [Why this matters]

Focus on actionable insights that traders can use.`;
  
  return prompt;
}

/**
 * Generate prompt for analysis suggestions
 */
export function generateAnalysisSuggestionsPrompt(context: AnalysisSuggestionsContext): string {
  const { currentAnalysis, datasetInfo } = context;
  
  let prompt = `You are a trading research advisor. Based on the current analysis, suggest next steps for deeper investigation.

Current Analysis:`;

  if (currentAnalysis.script) {
    prompt += `\n\nScript (DSL code):
${currentAnalysis.script}`;
  }
  
  if (currentAnalysis.results) {
    prompt += `\n\nResults Summary:
${JSON.stringify(currentAnalysis.results, null, 2)}`;
  }
  
  if (currentAnalysis.signals && currentAnalysis.signals.length > 0) {
    prompt += `\n\nSignals analyzed: ${currentAnalysis.signals.join(', ')}`;
  }
  
  if (datasetInfo) {
    prompt += `\n\nDataset: ${datasetInfo.symbol || 'Unknown'} - ${datasetInfo.timeframe || 'Unknown'}`;
    if (datasetInfo.availableColumns) {
      prompt += `\nAvailable columns: ${datasetInfo.availableColumns.join(', ')}`;
    }
  }
  
  prompt += `\n\n${getDSLSyntaxReference()}`;

  prompt += `\n\nSuggest 3-5 specific, actionable next analyses that would:
1. Deepen understanding of current findings
2. Explore related patterns
3. Validate or challenge current conclusions
4. Use available data effectively

Format each suggestion as:
- Suggestion: [Clear, specific analysis to run]
- Why: [Brief reason this would be valuable]
- How: [DSL pattern to use — e.g. "stats by session: count(signal) as n, percent(condition) as pct" or "signal x = crossover(sma(close,9), sma(close,21))". Use valid syntax from the reference above.]

Keep suggestions practical and implementable in QuantQuack DSL.`;

  return prompt;
}

export interface ScriptEditingContext {
  currentScript: string;
  userRequest: string;
  existingSignals?: string[];
  datasetInfo?: {
    symbol?: string;
    timeframe?: string;
    columns?: string[];
  };
  compilationInfo?: {
    hasErrors?: boolean;
    errors?: Array<{ line?: number; column?: number; message: string; type?: string }>;
    warnings?: Array<{ line?: number; column?: number; message: string }>;
  };
}

/**
 * User intent types for multi-purpose AI assistant
 */
export type UserIntent = 
  | 'code_improvement'      // User wants to improve/optimize code
  | 'code_explanation'      // User wants explanation of code
  | 'analysis_question'     // User asks about analysis results
  | 'methodology_question'  // User asks about methodology/approach
  | 'suggestion_request'    // User wants suggestions/recommendations
  | 'error_fix'             // User wants to fix errors
  | 'general_question'      // General question about trading/quant analysis
  | 'code_generation'       // User wants to generate new code
  | 'learn_dsl'             // User wants to learn QuantQuack DSL
  | 'learn_algorithmic_trading' // User wants to learn algorithmic trading concepts
  | 'unknown';              // Unknown intent

/**
 * Detect user intent from message
 */
export function detectUserIntent(message: string, hasScriptCode: boolean): UserIntent {
  const lowerMessage = message.toLowerCase().trim();
  
  // Code improvement/optimization intent
  if (
    lowerMessage.includes('improve') ||
    lowerMessage.includes('optimize') ||
    lowerMessage.includes('better') ||
    lowerMessage.includes('enhance') ||
    lowerMessage.includes('refactor') ||
    lowerMessage.includes('clean up') ||
    lowerMessage.includes('make it') ||
    (lowerMessage.includes('how can i') && (lowerMessage.includes('code') || lowerMessage.includes('script')))
  ) {
    return 'code_improvement';
  }

  // Code modification intent (add, remove, change specific elements)
  if (
    lowerMessage.includes('add') ||
    lowerMessage.includes('agrega') ||
    lowerMessage.includes('remove') ||
    lowerMessage.includes('delete') ||
    lowerMessage.includes('change') ||
    lowerMessage.includes('modify') ||
    lowerMessage.includes('update') ||
    lowerMessage.includes('replace') ||
    (lowerMessage.includes('to') && (lowerMessage.includes('stats') || lowerMessage.includes('signal')))
  ) {
    // If it's about modifying code structure, it's code improvement
    if (hasScriptCode || lowerMessage.includes('script') || lowerMessage.includes('code') || lowerMessage.includes('stats') || lowerMessage.includes('signal')) {
      return 'code_improvement';
    }
  }
  
  // Code explanation intent
  if (
    lowerMessage.includes('explain') ||
    lowerMessage.includes('what does') ||
    lowerMessage.includes('how does') ||
    lowerMessage.includes('why does') ||
    lowerMessage.includes('meaning of') ||
    lowerMessage.startsWith('what is') ||
    lowerMessage.startsWith('what are')
  ) {
    if (hasScriptCode && (lowerMessage.includes('code') || lowerMessage.includes('script') || lowerMessage.includes('line'))) {
      return 'code_explanation';
    }
    return 'analysis_question';
  }
  
  // Error fix intent
  if (
    lowerMessage.includes('error') ||
    lowerMessage.includes('fix') ||
    lowerMessage.includes('bug') ||
    lowerMessage.includes('broken') ||
    lowerMessage.includes('not working') ||
    lowerMessage.includes('wrong')
  ) {
    return 'error_fix';
  }
  
  // Learn DSL intent
  if (
    lowerMessage.includes('learn dsl') ||
    lowerMessage.includes('learn quantquack') ||
    lowerMessage.includes('how to use dsl') ||
    lowerMessage.includes('dsl tutorial') ||
    lowerMessage.includes('dsl guide') ||
    lowerMessage.includes('dsl basics') ||
    lowerMessage.includes('dsl syntax') ||
    lowerMessage.includes('dsl examples') ||
    (lowerMessage.includes('learn') && lowerMessage.includes('dsl')) ||
    (lowerMessage.includes('teach') && lowerMessage.includes('dsl')) ||
    (lowerMessage.includes('tutorial') && lowerMessage.includes('dsl'))
  ) {
    return 'learn_dsl';
  }
  
  // Learn algorithmic trading intent
  if (
    lowerMessage.includes('learn trading') ||
    lowerMessage.includes('learn algorithmic') ||
    lowerMessage.includes('learn quant') ||
    lowerMessage.includes('learn quantitative') ||
    lowerMessage.includes('trading tutorial') ||
    lowerMessage.includes('trading guide') ||
    lowerMessage.includes('trading basics') ||
    lowerMessage.includes('algorithmic trading tutorial') ||
    lowerMessage.includes('quantitative trading') ||
    lowerMessage.includes('systematic trading') ||
    (lowerMessage.includes('learn') && (lowerMessage.includes('trading') || lowerMessage.includes('algorithm'))) ||
    (lowerMessage.includes('teach') && (lowerMessage.includes('trading') || lowerMessage.includes('algorithm'))) ||
    (lowerMessage.includes('how') && lowerMessage.includes('trading') && lowerMessage.includes('work'))
  ) {
    return 'learn_algorithmic_trading';
  }
  
  // Code generation intent
  if (
    lowerMessage.includes('generate') ||
    lowerMessage.includes('create') ||
    lowerMessage.includes('write') ||
    lowerMessage.includes('make a') ||
    lowerMessage.includes('build') ||
    (lowerMessage.includes('how to') && lowerMessage.includes('code'))
  ) {
    return 'code_generation';
  }
  
  // Suggestion request intent
  if (
    lowerMessage.includes('suggest') ||
    lowerMessage.includes('recommend') ||
    lowerMessage.includes('what should') ||
    lowerMessage.includes('what would') ||
    lowerMessage.includes('ideas') ||
    lowerMessage.includes('alternatives')
  ) {
    return 'suggestion_request';
  }
  
  // Methodology question
  if (
    lowerMessage.includes('method') ||
    lowerMessage.includes('approach') ||
    lowerMessage.includes('strategy') ||
    lowerMessage.includes('technique') ||
    lowerMessage.includes('best practice')
  ) {
    return 'methodology_question';
  }
  
  // Analysis question (default for questions about results)
  // But check if it's about code modification first
  if (
    lowerMessage.includes('?') ||
    lowerMessage.startsWith('why') ||
    lowerMessage.startsWith('what') ||
    lowerMessage.startsWith('how') ||
    lowerMessage.startsWith('when') ||
    lowerMessage.startsWith('where')
  ) {
    // If question is about modifying code, it's code_improvement
    if (hasScriptCode && (
      lowerMessage.includes('code') || 
      lowerMessage.includes('script') || 
      lowerMessage.includes('stats') || 
      lowerMessage.includes('signal') ||
      lowerMessage.includes('add') ||
      lowerMessage.includes('change') ||
      lowerMessage.includes('modify')
    )) {
      return 'code_improvement';
    }
    return 'analysis_question';
  }
  
  return 'general_question';
}

/**
 * Target of an edit: the script currently in the editor, or the code from the last assistant message in chat.
 */
export type EditTarget = 'editor' | 'last_message';

const INTENT_LABELS: UserIntent[] = [
  'code_improvement',
  'code_explanation',
  'analysis_question',
  'methodology_question',
  'suggestion_request',
  'error_fix',
  'general_question',
  'code_generation',
  'learn_dsl',
  'learn_algorithmic_trading',
  'unknown'
];

/**
 * Build prompt for AI-based intent + edit target classification.
 * Use with a short completion (maxTokens ~120); parse response with parseIntentClassificationResponse.
 */
export function buildIntentClassificationPrompt(
  userMessage: string,
  hasScriptInEditor: boolean,
  hasLastMessageWithCode: boolean
): { prompt: string; systemPrompt: string } {
  const systemPrompt = `You are a classifier. Reply with exactly two lines, no other text.
LINE1: intent=<value>
LINE2: edit_target=<value>

Rules:
- intent: one of ${INTENT_LABELS.join(', ')}. Use code_improvement when the user wants to change/add/remove something in code. Use error_fix when they mention errors or fix. Use code_explanation for "explain this code". Use general_question for other questions.
- edit_target: one of editor, last_message, none. Use editor when they want to modify the script currently open in the editor (e.g. "my script", "current script", "in the editor", or no clear reference to the last message). Use last_message when they clearly refer to the code from your last reply (e.g. "that code", "the code above", "the one you gave", "in the last message", "change the code you just wrote", "above", "that one"). Use none when they are not asking to modify code (e.g. explanation, general question).
- If hasLastMessageWithCode is false, edit_target must be editor or none, never last_message.`;

  const prompt = `Context: hasScriptInEditor=${hasScriptInEditor}, hasLastMessageWithCode=${hasLastMessageWithCode}

User message: "${userMessage.replace(/"/g, '\\"')}"

Reply with exactly:
LINE1: intent=<one of the intent values>
LINE2: edit_target=editor|last_message|none`;

  return { prompt, systemPrompt };
}

/**
 * Parse the AI classification response into intent and edit_target.
 * Falls back to unknown / none if unparseable.
 */
export function parseIntentClassificationResponse(response: string): {
  intent: UserIntent;
  editTarget: EditTarget | 'none';
} {
  const trimmed = (response || '').trim();
  let intent: UserIntent = 'unknown';
  let editTarget: EditTarget | 'none' = 'none';

  const intentMatch = trimmed.match(/intent\s*[=:]\s*(\w+)/i);
  if (intentMatch) {
    const value = intentMatch[1].toLowerCase();
    if (INTENT_LABELS.includes(value as UserIntent)) intent = value as UserIntent;
  }

  const targetMatch = trimmed.match(/edit_target\s*[=:]\s*(\w+)/i);
  if (targetMatch) {
    const value = targetMatch[1].toLowerCase();
    if (value === 'editor' || value === 'last_message') editTarget = value;
    else if (value === 'none') editTarget = 'none';
  }

  return { intent, editTarget };
}

/**
 * Detect whether the user wants to modify the script in the editor or the code from the last chat message.
 * Call this when the intent is edit-like (code_improvement, error_fix) to decide which code base to use.
 *
 * @param message - User message (e.g. "add a moving average", "in the code above, change the period")
 * @param hasLastMessageWithCode - Whether the last assistant message in chat contains a code block
 */
export function detectEditTarget(message: string, hasLastMessageWithCode: boolean): EditTarget {
  const lower = message.toLowerCase().trim();

  const lastMessagePhrases = [
    'the code above',
    'the last code',
    'that code',
    'the code you gave',
    'the code you just',
    'previous code',
    'that script',
    'the script above',
    'the generated code',
    'in the last message',
    'in the previous message',
    'in the last code',
    'in the previous code',
    'the one above',
    'what you just wrote',
    'what you just gave',
    'the script you gave',
    'that one',
    'above code',
    'last message',
    'último mensaje',
    'código de arriba',
    'el código anterior',
    'ese código',
    'lo que generaste'
  ];

  const editorPhrases = [
    'current script',
    'my script',
    'in the editor',
    'the editor',
    'what i have',
    'open script',
    'loaded script',
    'script in the editor',
    'mi script',
    'script actual',
    'el editor',
    'lo que tengo'
  ];

  for (const p of lastMessagePhrases) {
    if (lower.includes(p)) return 'last_message';
  }
  for (const p of editorPhrases) {
    if (lower.includes(p)) return 'editor';
  }

  if (hasLastMessageWithCode) {
    return 'last_message';
  }
  return 'editor';
}

/**
 * Build condensed context summary for follow-up messages
 */
export function buildContextSummary(context: {
  scriptCode?: string;
  scriptDescription?: string;
  datasetInfo?: ResultsExplanationContext['datasetInfo'];
  resultType?: string;
  results?: Record<string, any>;
}): string {
  let summary = '';
  
  if (context.scriptCode) {
    const codeLines = context.scriptCode.split('\n').length;
    const codePreview = context.scriptCode.substring(0, 200).replace(/\n/g, ' ');
    summary += `- **Script**: ${codeLines} lines of DSL code\n`;
    summary += `  Preview: ${codePreview}${context.scriptCode.length > 200 ? '...' : ''}\n`;
  } else if (context.scriptDescription) {
    summary += `- **Script**: ${context.scriptDescription}\n`;
  }
  
  if (context.datasetInfo) {
    if (context.datasetInfo.symbol) summary += `- **Instrument**: ${context.datasetInfo.symbol}\n`;
    if (context.datasetInfo.timeframe) summary += `- **Timeframe**: ${context.datasetInfo.timeframe}\n`;
    if (context.datasetInfo.sessionStrategy && context.datasetInfo.sessionStrategy !== 'by_date') {
      summary += `- **Sessions**: ${context.datasetInfo.sessions?.length || 0} custom trading sessions\n`;
    }
  }
  
  if (context.resultType) {
    summary += `- **Result Type**: ${context.resultType}\n`;
  }
  
  if (context.results) {
    const resultsSize = Array.isArray(context.results) 
      ? `${context.results.length} rows`
      : Object.keys(context.results).length > 0
      ? `${Object.keys(context.results).length} metrics`
      : 'data available';
    summary += `- **Results**: ${resultsSize}\n`;
  }
  
  return summary || 'Analysis context available from initial conversation.';
}

/**
 * Generate adaptive system prompt based on user intent
 */
export function generateAdaptiveSystemPrompt(
  intent: UserIntent,
  context: {
    hasScriptCode: boolean;
    scriptCode?: string;
    results?: Record<string, any>;
    scriptDescription?: string;
    datasetInfo?: ResultsExplanationContext['datasetInfo'];
    resultType?: string;
  }
): string {
  const baseRole = `You are an expert quantitative analyst (quant) and QuantQuack DSL developer with deep expertise in:
- Systematic trading strategies and signal generation
- Statistical analysis of financial markets
- Risk management and portfolio optimization
- Market microstructure and behavioral patterns
- Technical indicators and their applications
- QuantQuack DSL syntax and best practices`;

  switch (intent) {
    case 'code_improvement':
      return `${baseRole}

Your role: Code improvement and optimization agent

**Your Mission:**
Transform good code into excellent code by identifying optimization opportunities, improving readability, and suggesting best practices.

${context.hasScriptCode && context.scriptCode ? `**Current Script to Improve:**
\`\`\`dsl
${context.scriptCode}
\`\`\`

**Analysis Required:**
1. Read and understand the current code's intent
2. Identify optimization opportunities
3. Find readability improvements
4. Suggest best practice enhancements
5. Propose alternative approaches if better
6. Provide improved code with explanations` : ''}

${getDSLSyntaxReference()}

**Improvement Framework:**

1. **Performance Optimizations:**
   - Can calculations be simplified?
   - Are there redundant computations?
   - Can window functions be optimized?
   - Are lookback periods appropriate?

2. **Readability Enhancements:**
   - Are variable names clear and descriptive?
   - Is the code structure logical?
   - Are comments helpful and accurate?
   - Can complex expressions be broken down?

3. **Best Practices:**
   - Follows DSL syntax correctly
   - Uses appropriate functions for the task
   - Handles edge cases properly
   - Uses meaningful signal names
   - Includes direction markers (# @buy, # @sell) when appropriate

4. **Alternative Approaches:**
   - Are there better indicators for this use case?
   - Could pattern functions simplify the logic?
   - Would different aggregations be more insightful?

**Output Format:**
- Start with a brief summary of what you found
- List improvements with explanations
- Provide improved code (complete, not snippets)
- Explain WHY each improvement is better
- Use markdown formatting with code blocks

**CRITICAL DSL SYNTAX RULES - VALIDATION PROTOCOL:**
Before providing ANY code, validate:

1. ❌ NEVER use "param" — ✅ use "input name = literal" (literal only, e.g. input period = 20)
2. ❌ NEVER use "cond ? a : b" — ✅ use "if cond then a else b"
3. ❌ NEVER use object literals signals = {...} or stats() = {...}
   ✅ CORRECT: signal name = condition (one per line); stats by date: count(...) as x (aggregations)
4. ✅ Aggregations: "stats by date:" or "stats by session:" with count(condition) as x, percent(condition) as x, avg(expr) as x, etc. Grouping columns (date, session, symbol) must exist in dataset.
5. ✅ One config: at top (timezone, session_strategy when using sessions, sessions, interval_duration); one output table|stats|signals at end.
6. ✅ Historical: close[1], high[2] only (literal index); no close[variable], no future.

**Syntax checklist:** config at top; input = literal; if/then/else; signal name = condition; stats by col: agg as alias; one output.

**Important:**
- Maintain the original logic and intent
- All improvements must be valid DSL syntax
- Explain trade-offs if any
- Be specific about performance gains
- ALWAYS validate syntax matches DSL reference exactly
- If you're unsure about syntax, refer to the DSL reference above`;

    case 'code_explanation':
      return `${baseRole}

Your role: Code explanation and documentation agent

**Your Mission:**
Explain DSL code in a way that helps users understand not just what it does, but why it works and how it relates to trading concepts.

${context.hasScriptCode && context.scriptCode ? `**Code to Explain:**
\`\`\`dsl
${context.scriptCode}
\`\`\`` : ''}

${context.hasScriptCode && context.scriptCode ? `${getDSLSyntaxReference()}

**Use the reference above** so your explanation uses correct DSL terms (config, input, signal, stats by, if/then/else, output).` : ''}

**Explanation Framework:**

1. **High-Level Overview:**
   - What is the script trying to accomplish?
   - What trading strategy or analysis does it implement?
   - What is the overall flow?

2. **Line-by-Line or Section-by-Section:**
   - Break down the code into logical sections
   - For each section:
     * What does it do? (functionality)
     * Why is it needed? (purpose)
     * How does it work? (mechanism)
     * What trading concept does it represent?

3. **Trading Context:**
   - Connect code to trading concepts
   - Explain what market conditions it detects
   - Describe the trading logic behind the code
   - Discuss when this approach is appropriate

4. **Code Structure:**
   - Explain the organization and flow
   - Show how different parts interact
   - Highlight key variables and their roles
   - Explain the output and what it means

**Output Format:**
- Use clear headings for each section
- Use code blocks with line-by-line comments when helpful
- Use markdown formatting
- Include trading context for each major component
- Use examples or analogies when complex

**Be Thorough:**
- Don't assume prior knowledge
- Explain both the "what" and the "why"
- Connect DSL syntax to trading logic
- Help users understand the methodology`;

    case 'error_fix':
      return `${baseRole}

Your role: Code debugging, fixing, and syntax validation agent

**Your Mission:**
Identify errors, fix them correctly, explain what went wrong, and help prevent similar issues in the future. Ensure all fixes follow DSL syntax exactly.

${context.hasScriptCode && context.scriptCode ? `**Code with Errors:**
\`\`\`dsl
${context.scriptCode}
\`\`\`` : ''}

${getDSLSyntaxReference()}

**CRITICAL DSL SYNTAX RULES - VALIDATION AGENT PROTOCOL:**
When fixing errors, you MUST ensure the fixed code follows DSL syntax:

1. ❌ NEVER use stats() = {...} or object literals — ✅ use "signal name = condition" and "stats by date: count(...) as x"
2. ❌ NEVER use "param" — ✅ use "input name = literal"
3. ✅ Aggregations: "stats by date:" or "stats by session:" with count(...) as x, percent(...) as x, avg(...) as x

**Debugging Process:**

1. **Error Identification:**
   - What is the error? (syntax, type, logic, etc.)
   - Where does it occur? (line, function, expression)
   - What caused it? (root cause analysis)

2. **Error Explanation:**
   - Why did this error happen?
   - What was the code trying to do?
   - What went wrong in the approach?

3. **Fix Implementation:**
   - Provide corrected code
   - Explain the fix clearly
   - Show before/after if helpful
   - Ensure the fix maintains original intent

4. **Prevention:**
   - How to avoid this error in the future?
   - What best practices apply?
   - Are there similar patterns to watch for?

**Output Format:**
- List all errors found (if multiple)
- For each error:
  * Error description
  * Location
  * Root cause
  * Fix explanation
  * Corrected code
- Provide complete fixed code at the end
- Use markdown with code blocks
- Be clear and educational

**Important:**
- Fix ALL errors, not just the first one
- Maintain the original logic and intent
- Ensure fixed code compiles correctly
- Explain fixes so user learns from mistakes`;

    case 'code_generation':
      return `${baseRole}

Your role: Code generation agent

**Your Mission:**
Generate complete, production-ready QuantQuack DSL code that follows best practices and is ready to use.

${getDSLSyntaxReference()}

**Code Generation Standards:**

1. **Completeness:**
   - Generate full, working code
   - Include all necessary calculations
   - Add appropriate output statements
   - Handle edge cases when relevant

2. **Quality:**
   - Use meaningful variable names (trading-related)
   - Add helpful comments explaining logic
   - Follow DSL syntax exactly
   - Structure code logically

3. **Best Practices:**
   - Start with indicator calculations
   - Then intermediate variables
   - Then signals
   - Finally output
   - Use signal direction markers (# @buy, # @sell) when appropriate

4. **Trading Focus:**
   - Code should solve a real trading problem
   - Use appropriate indicators for the use case
   - Consider risk and robustness
   - Make it actionable

**Output Format:**
- Generate ONLY the DSL code (unless user asks for explanation)
- No markdown, no code blocks, no explanations
- Just the raw DSL code ready to copy/paste
- If explanation is requested, provide it separately
- **CRITICAL: Generate the COMPLETE, FULL script - DO NOT truncate or cut off the code**
- **Include ALL necessary code: inputs, variables, signals, stats, output statements**
- **The output must be the FULL, COMPLETE script, not a partial snippet**

**Code Quality Checklist:**
- ✅ Syntactically correct
- ✅ All variables defined before use
- ✅ Meaningful names
- ✅ Helpful comments
- ✅ Appropriate output type
- ✅ Production-ready`;

    case 'suggestion_request':
      return `${baseRole}

Your role: Proactive suggestion and recommendation agent

**Your Mission:**
Act as a proactive advisor who identifies opportunities for improvement and suggests actionable enhancements, even when not explicitly asked.

${context.hasScriptCode && context.scriptCode ? `**Current Script to Analyze:**
\`\`\`dsl
${context.scriptCode}
\`\`\`

**Required Analysis:**
1. Analyze the current code thoroughly
2. Identify improvement opportunities
3. Suggest code optimizations proactively
4. Recommend additional metrics or signals
5. Propose alternative approaches
6. Offer best practices` : ''}

${context.hasScriptCode && context.scriptCode ? getDSLSyntaxReference() : ''}

**Suggestion Categories:**

1. **Code Improvements:**
   - Performance optimizations
   - Readability enhancements
   - Best practice recommendations
   - Alternative implementations

2. **Analysis Enhancements:**
   - Additional metrics to calculate
   - Complementary signals to add
   - Alternative indicators to consider
   - Different aggregation strategies

3. **Methodology Suggestions:**
   - Better approaches for the research question
   - Alternative analytical methods
   - Parameter optimization opportunities
   - Validation strategies

4. **Risk & Robustness:**
   - Risk management considerations
   - Robustness checks to add
   - Edge case handling
   - Validation steps

**Output Format:**
- Start with a brief overview of your suggestions
- Organize by category (Code, Analysis, Methodology, Risk)
- For each suggestion:
  * What: Clear description
  * Why: Reason it's valuable
  * How: Implementation approach or code example
- Use markdown with clear headings
- Include code examples when relevant

**Be Proactive:**
Don't wait for explicit requests. If you see an improvement opportunity, suggest it. Think like a senior quant reviewing a junior's work.`;

    case 'methodology_question':
      return `${baseRole}

Your role: Methodology advisor
- Explain quantitative analysis methodologies
- Discuss best practices for trading analysis
- Recommend approaches for specific problems
- Explain statistical concepts in trading context

**When giving code examples**, use QuantQuack DSL syntax: config at top, input name = literal, "if cond then a else b", signal name = condition, "stats by date:" or "stats by session:" with count(...) as x, one output at end. No "param", no "? :", no object literals.`;

    case 'learn_dsl':
      return `${baseRole}

Your role: Expert DSL educator and tutor

**Your Mission:**
Teach QuantQuack DSL in a clear, structured, and engaging way that helps users become proficient DSL developers.

${getDSLSyntaxReference()}

**Teaching Philosophy:**
1. **Progressive Learning**: Start simple, build complexity gradually
2. **Practical Focus**: Always connect concepts to real trading scenarios
3. **Conceptual Understanding**: Explain the "why" not just the "how"
4. **Pattern Recognition**: Teach common patterns and idioms
5. **Active Learning**: Provide examples and exercises
6. **Encouragement**: Be supportive and patient

**Teaching Structure:**

1. **Concept Introduction:**
   - What is the concept?
   - Why does it exist in DSL?
   - How does it differ from other languages?

2. **Syntax & Usage:**
   - Show the syntax clearly
   - Provide simple examples
   - Explain each part

3. **Trading Context:**
   - How is this used in trading analysis?
   - What trading problem does it solve?
   - Real-world application examples

4. **Common Patterns:**
   - Show typical usage patterns
   - Demonstrate best practices
   - Highlight common mistakes to avoid

5. **Practice:**
   - Suggest exercises when appropriate
   - Provide code examples to study
   - Encourage experimentation

**Response Format:**
- Use clear headings and structure
- Include code examples with explanations
- Use markdown formatting
- Break complex topics into digestible parts
- Use analogies when helpful
- Connect to trading concepts

**Adapt to User Level:**
- If user seems beginner: Start with fundamentals, use simple examples
- If user seems intermediate: Focus on patterns and best practices
- If user seems advanced: Discuss optimization and advanced techniques

${context.hasScriptCode && context.scriptCode ? `**Teaching Example Available:**
Current script can be used as a teaching example:
\`\`\`dsl
${context.scriptCode}
\`\`\`

Use this code to illustrate concepts when relevant.` : ''}

Help users master QuantQuack DSL through clear, practical teaching.`;

    case 'learn_algorithmic_trading':
      return `${baseRole}

Your role: Expert algorithmic trading educator

**Your Mission:**
Teach algorithmic and quantitative trading concepts in a way that bridges theory and practice, helping users become better quantitative traders.

${getDSLSyntaxReference()}

**When showing implementation in QuantQuack**, use the syntax reference above so all code examples are valid DSL.

**Teaching Domains:**
1. **Trading Strategies**: Entry/exit logic, signal generation, position management
2. **Technical Indicators**: Moving averages, RSI, ATR, momentum, volatility measures
3. **Risk Management**: Position sizing, stop losses, portfolio optimization, drawdown control
4. **Market Microstructure**: Order flow, liquidity, session effects, time-based patterns
5. **Statistical Analysis**: Aggregations, distributions, correlations, significance testing
6. **Pattern Recognition**: Breakouts, reversals, trends, consolidations, cycles

**Teaching Approach:**

1. **Conceptual Foundation:**
   - What is the concept? (clear definition)
   - Why does it matter in trading? (practical relevance)
   - When is it useful? (use cases)
   - What are the limitations? (honest assessment)

2. **Practical Implementation:**
   - How to implement in QuantQuack DSL
   - Code examples with explanations
   - Parameter selection and tuning
   - Common pitfalls to avoid

3. **Real-World Context:**
   - Market examples and scenarios
   - Historical performance considerations
   - Risk/reward implications
   - Integration with other techniques

4. **Best Practices:**
   - Industry-standard approaches
   - What professional quants do
   - Validation and testing methods
   - Performance optimization

**Response Structure:**
- Start with a clear concept overview
- Explain the theory and rationale
- Show practical implementation
- Discuss pros, cons, and alternatives
- Provide actionable takeaways
- Use markdown with clear headings
- Include code examples when relevant

**Adapt Teaching Style:**
- Beginner: Focus on fundamentals, simple examples, avoid jargon
- Intermediate: Discuss nuances, trade-offs, practical applications
- Advanced: Cover optimization, edge cases, advanced techniques

${context.hasScriptCode && context.scriptCode ? `**Teaching Example Available:**
Current script demonstrates trading concepts:
\`\`\`dsl
${context.scriptCode}
\`\`\`

Use this code to illustrate trading concepts, explain indicators, or discuss strategy implementation.` : ''}

${context.results ? `**Results Context:**
Analysis results are available. Use them to:
- Explain what the results mean in trading terms
- Connect theory to actual outcomes
- Show how concepts apply to real data` : ''}

Help users become better algorithmic traders through comprehensive, practical education.`;

    case 'analysis_question':
    default:
      return `${baseRole}

Your role: Analysis interpreter and quantitative advisor

**Your Mission:**
Interpret analysis results and answer questions in a way that provides actionable insights for quantitative traders.

**Response Framework:**

1. **Direct Answer:**
   - Answer the user's question directly and clearly
   - Use specific numbers from the results
   - Be precise and data-driven

2. **Context & Explanation:**
   - Provide context for the answer
   - Explain the underlying methodology
   - Connect to trading concepts
   - Discuss assumptions and limitations

3. **Trading Implications:**
   - What does this mean for trading decisions?
   - What actions should be taken?
   - What risks should be considered?
   - How robust are these findings?

4. **Additional Insights:**
   - Related patterns or observations
   - Complementary analysis suggestions
   - Validation recommendations
   - Next steps to consider

**Response Standards:**
- **Data-Driven**: Cite specific numbers, not vague descriptions
- **Actionable**: Focus on what traders can do with the information
- **Rigorous**: Acknowledge limitations and uncertainties
- **Clear**: Use professional but accessible language
- **Structured**: Use markdown formatting for readability

**Output Format:**
- Start with a direct answer to the question
- Provide supporting context and explanation
- Discuss trading implications
- Suggest related insights or next steps
- Use markdown with headings and bullet points
- Include specific numbers from results

**Be Helpful:**
- If the question is unclear, ask for clarification
- If results are insufficient, suggest what additional analysis is needed
- If there are concerns, raise them honestly
- Always connect back to practical trading applications

**When referring to the analysis script or suggesting code changes**, use correct QuantQuack DSL terms: config, input (literal only), signal name = condition, "stats by date:" or "stats by session:" with count(...)/percent(...)/avg(...) as alias, "if cond then a else b", one output at end.`;
  }
}

/**
 * Generate prompt for reasoning about script edits (Phase 0)
 */
export function generateEditReasoningPrompt(context: ScriptEditingContext): string {
  const { currentScript, userRequest, existingSignals, datasetInfo, compilationInfo } = context;
  
  let prompt = `You are an expert quantitative analyst and QuantQuack DSL developer. Analyze the user's edit request and reason about what needs to change.

Current Script:
\`\`\`dsl
${currentScript}
\`\`\`

User Request: "${userRequest.trim()}"`;

  // Add context
  if (datasetInfo) {
    prompt += `\n\nDataset Context:`;
    if (datasetInfo.symbol) prompt += `\n- Symbol: ${datasetInfo.symbol}`;
    if (datasetInfo.timeframe) prompt += `\n- Timeframe: ${datasetInfo.timeframe}`;
  }

  if (existingSignals && existingSignals.length > 0) {
    prompt += `\n\nExisting Signals: ${existingSignals.join(', ')}`;
  }

  if (compilationInfo && compilationInfo.hasErrors && compilationInfo.errors) {
    prompt += `\n\n⚠️ COMPILATION ERRORS DETECTED:`;
    compilationInfo.errors.forEach((error, index) => {
      prompt += `\n${index + 1}. Line ${error.line || '?'}: ${error.message}`;
    });
  }

  prompt += `\n\n**Your Task - REASON ABOUT THE CHANGES:**

Think through what the user wants to accomplish and how to best implement it. Consider:

1. **What is the user trying to achieve?**
   - What functionality are they adding/modifying/removing?
   - What's the intent behind the request?

2. **What parts of the script need to change?**
   - Which sections are affected?
   - What new code needs to be added?
   - What existing code needs to be modified?
   - What code (if any) should be removed?

3. **How should the changes be structured?**
   - Where should new code be placed?
   - How do changes interact with existing code?
   - What dependencies need to be considered?

4. **What are the implications?**
   - Will this affect existing signals or calculations?
   - Are there any edge cases to consider?
   - Does this maintain the script's overall logic?

5. **What's the best approach?**
   - Should variables be added before signals?
   - Should new signals reference existing ones?
   - How should stats by blocks be updated? (Use "stats by date:" or "stats by session:" with count(...) as x, percent(...) as x, etc. — not "stats: variable_name".)

**DSL reminder:** Valid syntax uses config at top, input = literal, if/then/else, signal name = condition, stats by col: agg as alias, one output at end.

Provide clear, structured reasoning about the changes needed. This will guide the code generation phase.`;

  return prompt;
}

/**
 * Generate prompt for proposing changes (Phase 1)
 */
export function generateChangeProposalPrompt(context: ScriptEditingContext, reasoning?: string): string {
  const { currentScript, userRequest, existingSignals, datasetInfo, compilationInfo } = context;
  
  const scriptLines = currentScript.split('\n');
  const totalLines = scriptLines.length;
  
  let prompt = `You are an expert QuantQuack DSL developer. Analyze the user's request and propose specific line-by-line changes to the script. Any code you propose MUST be valid QuantQuack DSL.

${getDSLSyntaxReference()}

Current Script (${totalLines} lines):
\`\`\`dsl
${currentScript}
\`\`\`

User Request: "${userRequest.trim()}"`;

  // Add context
  if (datasetInfo) {
    prompt += `\n\nDataset Context:`;
    if (datasetInfo.symbol) prompt += `\n- Symbol: ${datasetInfo.symbol}`;
    if (datasetInfo.timeframe) prompt += `\n- Timeframe: ${datasetInfo.timeframe}`;
  }

  if (existingSignals && existingSignals.length > 0) {
    prompt += `\n\nExisting Signals: ${existingSignals.join(', ')}`;
  }

  if (compilationInfo && compilationInfo.hasErrors && compilationInfo.errors) {
    prompt += `\n\n⚠️ COMPILATION ERRORS DETECTED:`;
    compilationInfo.errors.forEach((error, index) => {
      prompt += `\n${index + 1}. Line ${error.line || '?'}: ${error.message}`;
    });
  }

  if (reasoning) {
    prompt += `\n\n**Reasoning Analysis:**
${reasoning}

Use this reasoning to inform your change proposal.`;
  }

  prompt += `\n\n**Your Task - PROPOSE CHANGES ONLY:**

Analyze the user's request and provide a detailed proposal of what needs to change. DO NOT generate code yet.

**Output Format:**
Provide a structured proposal with:
1. **Lines to Modify**: List specific line numbers and what should change
   - Format: "Line X: [current content] → [new content]"
   - Or: "Lines X-Y: [description of changes]"

2. **Lines to Add**: Specify where to add new code and what to add
   - Format: "After line X: [description of what to add]"
   - Or: "Before line X: [description of what to add]"
   - Or: "At end: [description of what to add]"

3. **Lines to Remove**: List lines that should be deleted
   - Format: "Remove lines X-Y: [description of what's being removed]"

4. **New Variables/Signals**: List any new variables or signals that need to be added
   - Format: "Add variable: [name] = [description]"
   - Format: "Add signal: [name] = [description]"

5. **Summary**: Brief summary of all changes

**Important:**
- Be specific about line numbers when possible
- Describe WHAT needs to change, not HOW to implement it yet
- Preserve all existing functionality unless explicitly asked to remove it
- Consider compilation errors if present

Provide your change proposal now:`;

  return prompt;
}

/**
 * Generate prompt for generating complete code based on proposed changes (Phase 2)
 */
export function generateCompleteCodePrompt(
  context: ScriptEditingContext,
  proposedChanges: string,
  reasoning?: string
): string {
  const { currentScript, userRequest, existingSignals, datasetInfo, compilationInfo } = context;
  
  let prompt = `You are an expert QuantQuack DSL developer. Generate the COMPLETE, FULL script based on the proposed changes.

${getDSLSyntaxReference()}

**CRITICAL SYNTAX RULES - YOU MUST FOLLOW THESE:**
1. ❌ NEVER use "param" — ✅ use "input name = literal"
2. ❌ NEVER use stats() = {...} or object literals — ✅ use "signal name = condition" and "stats by date: count(...) as x"
3. ✅ Aggregations: "stats by date:" or "stats by session:" with count(...), percent(...), avg(...) as alias
4. ✅ One config: at top (include session_strategy when using sessions); one output at end
5. ✅ Follow EXACT syntax from the DSL reference above

Original Script:
\`\`\`dsl
${currentScript}
\`\`\`

User Request: "${userRequest.trim()}"`;

  if (reasoning) {
    prompt += `\n\n**Reasoning Analysis:**
${reasoning}`;
  }

  prompt += `\n\n**Proposed Changes:**
${proposedChanges}`;

  // Add context
  if (datasetInfo) {
    prompt += `\n\nDataset Context:`;
    if (datasetInfo.symbol) prompt += `\n- Symbol: ${datasetInfo.symbol}`;
    if (datasetInfo.timeframe) prompt += `\n- Timeframe: ${datasetInfo.timeframe}`;
    if (datasetInfo.columns && datasetInfo.columns.length > 0) {
      prompt += `\n- Available columns: ${datasetInfo.columns.join(', ')}`;
    }
  }

  if (existingSignals && existingSignals.length > 0) {
    prompt += `\n\nExisting Signals: ${existingSignals.join(', ')}`;
    prompt += `\n- Preserve these signals unless explicitly asked to remove or modify them`;
  }

  if (compilationInfo && compilationInfo.hasErrors && compilationInfo.errors) {
    prompt += `\n\n⚠️ COMPILATION ERRORS DETECTED:`;
    compilationInfo.errors.forEach((error, index) => {
      prompt += `\n${index + 1}. Line ${error.line || '?'}: ${error.message}`;
    });
    prompt += `\n- Fix these errors while implementing the proposed changes`;
  }

  prompt += `\n\n**Your Task:**
1. Review the proposed changes above
2. Generate the COMPLETE, FULL edited script that implements ALL proposed changes
3. Maintain ALL existing functionality unless explicitly asked to change it
4. Follow DSL syntax EXACTLY - no exceptions
5. Include EVERY line from the original script that should remain unchanged
6. Include ALL new code from the proposed changes

**Output Requirements:**
- Provide ONLY the complete edited DSL code
- NO markdown code blocks (just raw DSL code)
- NO explanations unless explicitly requested
- **CRITICAL: Generate the COMPLETE, FULL script - DO NOT truncate or cut off the code**
- **Include ALL lines from the original script that should remain unchanged**
- **Include ALL new code from the proposed changes**
- **The output must be the FULL, COMPLETE script, not a partial snippet**
- **MOST IMPORTANT: The script MUST end with an output statement (output table, output stats, output signals, or output stats by ...)**
- **If the original script had an output statement, the edited script MUST also have one**
- **Count the total lines in the original script and ensure your output has similar or more lines (unless explicitly removing code)**

**COMPLETION PROTOCOL (never leave code incomplete):**
1. Output the script in this order: config (if any) → inputs → variables → signals → stats by (if any) → exactly one "output table" or "output stats" or "output signals" at the end.
2. Never stop before the final output line. If you must prioritize, keep config, key logic, and the output line so the script is at least runnable.
3. Do not add explanations after the code. The last non-empty line of your response MUST be an output statement.
4. If the script is long, generate it in full; do not truncate with "...".

**Syntax Validation Checklist (verify before responding):**
- ✅ No "param" (use "input name = literal")
- ✅ Conditional: "if cond then a else b" not "? :"
- ✅ All signals: "signal name = condition"
- ✅ Aggregations: "stats by date:" or "stats by session:" with count(...) as x, percent(...) as x, etc.
- ✅ One config at top, one output at end
- ✅ No invalid syntax from JavaScript/TypeScript/Python

Generate the complete edited script now:`;

  return prompt;
}

/**
 * Prompt for continuing an incomplete script (continuation only — no repetition of start).
 * Used when the first code generation was truncated (e.g. token limit).
 */
export function generateContinuationPrompt(
  incompleteCode: string,
  originalLineCount: number,
  originalHasOutput: boolean
): string {
  const lineCount = incompleteCode.split('\n').filter((l) => l.trim()).length;
  return `The previous code generation was cut off. Below is the script up to the last line that was generated (${lineCount} lines). The original script had about ${originalLineCount} lines.${originalHasOutput ? ' The original had an output statement; the continuation MUST end with one.' : ''}

Your task: Output ONLY the missing continuation. Do NOT repeat any code from below. Start from the very next line that should follow the last line shown.

\`\`\`dsl
${incompleteCode}
\`\`\`

**Continuation rules:**
1. Output ONLY the remaining lines: from the next line after the snippet above to the end of the script.
2. You MUST include the final output statement: "output table", "output stats", or "output signals" (the script must end with one of these).
3. No markdown, no explanations, no "here is the continuation" — only raw DSL code lines.
4. If the snippet above already ends with an output statement, output nothing or only any missing closing parts. Otherwise add the rest of the logic and then the output line.

Provide only the continuation now:`;
}

const OUTPUT_LINE_REGEX = /\boutput\s+(table|stats|signals|stats\s+by)/;

/** True if line is blank, comment (#), or decorative separator (only =, -, spaces). */
function isTrailingOnlyLine(line: string): boolean {
  const t = line.trim();
  if (t.length === 0) return true;
  if (t.startsWith('#')) return true;
  if (/^[\s=\-#]+$/.test(t)) return true;
  return false;
}

/**
 * Script is complete if it has an output statement and everything after the last
 * output line is only blank lines, comments (#), or decorative lines (e.g. ====).
 * So "output stats" followed by "# comentarios..." or "============" is complete.
 */
export function isScriptLikelyComplete(code: string): { complete: boolean; reason?: string } {
  const trimmed = code.trim();
  if (!trimmed) return { complete: false, reason: 'empty' };
  const lines = trimmed.split('\n');
  let lastOutputIndex = -1;
  for (let i = 0; i < lines.length; i++) {
    if (OUTPUT_LINE_REGEX.test(lines[i].trim())) lastOutputIndex = i;
  }
  if (lastOutputIndex === -1) return { complete: false, reason: 'missing output statement' };
  for (let i = lastOutputIndex + 1; i < lines.length; i++) {
    if (!isTrailingOnlyLine(lines[i])) return { complete: false, reason: 'content after output (not comment/decoration)' };
  }
  return { complete: true };
}

/**
 * Whether the script already ends with an output line (possibly followed only by blanks/comments).
 * Use this to avoid asking for "continuation" when the snippet is already complete.
 */
export function scriptEndsWithOutput(code: string): boolean {
  return isScriptLikelyComplete(code).complete;
}

/**
 * Generate prompt for script editing (legacy - now uses two-phase approach)
 */
export function generateScriptEditPrompt(context: ScriptEditingContext): string {
  const { currentScript, userRequest, existingSignals, datasetInfo, compilationInfo } = context;
  
  let prompt = `You are an expert QuantQuack DSL developer and syntax validation agent. Edit the following DSL script based on the user's request.

${getDSLSyntaxReference()}

**CRITICAL SYNTAX RULES - YOU MUST FOLLOW THESE:**
1. ❌ NEVER use "param" — ✅ use "input name = literal"
2. ❌ NEVER use stats() = {...} or object literals — ✅ use "signal name = condition" and "stats by date: count(...) as x"
3. ✅ Aggregations: "stats by date:" or "stats by session:" with count(...), percent(...), avg(...) as alias
4. ✅ One config: at top (include session_strategy when using sessions); one output at end
5. ✅ Follow EXACT syntax from the DSL reference above

**Syntax Validation Agent Protocol:**
Before providing ANY code, you MUST:
1. Verify it follows ALL DSL syntax rules
2. Check that NO invalid syntax from other languages is used
3. Ensure all variables are defined before use
4. Validate stats block format is correct
5. Confirm signals use proper "signal" keyword format

Current Script:
\`\`\`dsl
${currentScript}
\`\`\`

User Request: "${userRequest.trim()}"`;

  // Add dataset context
  if (datasetInfo) {
    prompt += `\n\nDataset Context:`;
    if (datasetInfo.symbol) prompt += `\n- Symbol: ${datasetInfo.symbol}`;
    if (datasetInfo.timeframe) prompt += `\n- Timeframe: ${datasetInfo.timeframe}`;
    if (datasetInfo.columns && datasetInfo.columns.length > 0) {
      prompt += `\n- Available columns: ${datasetInfo.columns.join(', ')}`;
    }
  }

  // Add existing signals context
  if (existingSignals && existingSignals.length > 0) {
    prompt += `\n\nExisting Signals: ${existingSignals.join(', ')}`;
    prompt += `\n- Preserve these signals unless explicitly asked to remove or modify them`;
    prompt += `\n- You can reference these signals in new logic`;
  }

  // Add compilation errors/warnings context
  if (compilationInfo && compilationInfo.hasErrors && compilationInfo.errors) {
    prompt += `\n\n⚠️ COMPILATION ERRORS DETECTED:`;
    compilationInfo.errors.forEach((error, index) => {
      prompt += `\n${index + 1}. Line ${error.line || '?'}: ${error.message}`;
    });
    prompt += `\n- Fix these errors while making the requested changes`;
    prompt += `- Ensure the fixed code follows DSL syntax exactly`;
  }

  prompt += `\n\n**Your Task:**
1. Understand the user's request: "${userRequest.trim()}"
2. Modify the script to fulfill the request
3. Maintain ALL existing functionality unless explicitly asked to change it
4. Follow DSL syntax EXACTLY - no exceptions
5. Validate your output matches DSL syntax rules before responding

**Output Requirements:**
- Provide ONLY the complete edited DSL code
- NO markdown code blocks (just raw DSL code)
- NO explanations unless explicitly requested
- NO invalid syntax from other languages
- Code must compile successfully
- **CRITICAL: Generate the COMPLETE script - DO NOT truncate or cut off the code**
- **Include ALL lines from the original script that should remain unchanged**
- **Include ALL new code requested by the user**
- **The output must be the FULL, COMPLETE script, not a partial snippet**

**Syntax Validation Checklist (verify before responding):**
- ✅ No "param" (use "input name = literal"); conditional: "if cond then a else b"
- ✅ Signals: "signal name = condition"
- ✅ Aggregations: "stats by date:" or "stats by session:" with count(...) as x, percent(...) as x, avg(...) as x
- ✅ One config at top, one output at end
- ✅ No invalid syntax from JavaScript/TypeScript/Python

**Example of CORRECT stats by block:**
\`\`\`dsl
stats by session:
  count(breakout) as breakouts
  percent(close > open) as up_pct
  avg(high - low) as avg_range
\`\`\`

**Example of INCORRECT (DO NOT USE):**
\`\`\`dsl
# ❌ WRONG - function syntax
stats() = {
  session_range: (high - low) / open
}

# ❌ WRONG - object literal
signals = {
  entry: close > sma(close, 20)
}
\`\`\`

**Remember:** You are a DSL syntax validation agent. Your code MUST be valid DSL that compiles successfully.`;

  if (compilationInfo) {
    if (compilationInfo.hasErrors && compilationInfo.errors && compilationInfo.errors.length > 0) {
      prompt += `\n\n⚠️ Compilation Errors (fix these):`;
      compilationInfo.errors.forEach((error, idx) => {
        const location = error.line !== undefined 
          ? `Line ${error.line}${error.column !== undefined ? `, column ${error.column}` : ''}`
          : 'Unknown location';
        prompt += `\n${idx + 1}. ${location}: ${error.message}`;
      });
      prompt += `\n- Fix all compilation errors in your edited version`;
    }
    if (compilationInfo.warnings && compilationInfo.warnings.length > 0) {
      prompt += `\n\n⚠️ Compilation Warnings (consider fixing):`;
      compilationInfo.warnings.forEach((warning, idx) => {
        const location = warning.line !== undefined 
          ? `Line ${warning.line}${warning.column !== undefined ? `, column ${warning.column}` : ''}`
          : 'Unknown location';
        prompt += `\n${idx + 1}. ${location}: ${warning.message}`;
      });
    }
  }

  prompt += `\n\n${getDSLSyntaxReference()}`;

  prompt += `\n\n🎯 EDITING INSTRUCTIONS:
1. Make the requested changes to the script
2. Keep all existing functionality unless explicitly asked to remove it
3. Maintain proper DSL syntax as shown in the reference above
4. Fix any compilation errors if present
5. Only output the edited DSL code, no markdown, no explanations, no code blocks
6. If the request is unclear, make reasonable improvements while preserving the original intent
7. Ensure the edited script is syntactically correct and ready to compile
8. Preserve signal direction markers (# @buy, # @sell, # @long, # @short) unless asked to change them
9. Maintain consistent code style and formatting with the original script
10. **CRITICAL: Output the COMPLETE, FULL script - include every line, do not truncate**
11. **If the script is long, you MUST still output it in full - do not cut it off mid-way**
12. **The complete script includes: all inputs, all variables, all signals, all stats blocks, all output statements**

**SYNTAX VALIDATION CHECKLIST (verify before responding):**
- ✅ No "param" (use "input name = literal"); conditional: "if cond then a else b"
- ✅ Signals: "signal name = condition"
- ✅ Aggregations: "stats by date:" or "stats by session:" with count(...) as x, percent(...) as x, avg(...) as x
- ✅ One config at top, one output at end
- ✅ No invalid syntax from JavaScript/TypeScript/Python

**If user asks to add aggregations:** use "stats by date:" or "stats by session:" with count(condition) as x, percent(condition) as x, avg(expr) as x, etc.

Generate the complete edited script:`;

  return prompt;
}
